import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, useContext } from 'react';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { flushSync } from 'react-dom';
import { CaretCircleUpDownIcon, SpinnerGapIcon, PlayIcon } from '@phosphor-icons/react';
import {
  BELOW_STACK_SPLITTER_TOTAL_PX,
  BELOW_STACK_TRANSCRIPT_MIN_PX,
  FULLSCREEN_ZOOM_BELOW_TRANSCRIPT_MIN_PX,
  BELOW_STACK_VIDEO_MIN_PX,
  SIDE_STACK_SPLITTER_TOTAL_PX,
  SIDE_TRANSCRIPT_MIN_WIDTH_PX,
  SIDE_PLAYER_MIN_WIDTH_PX,
  AUDIO_SIDE_PLAYER_MIN_WIDTH_PX,
} from '@/shared/below-stack-layout';
import {
  handleDownload,
  getMediaType,
  formatTimeIndex,
  getSecondsFromTimeString,
  encodeEvidenceLockerStyleProxyUriSegment,
  sanitizeHttpUrlForBrowserLoad,
  resolveMediaPlaybackObjectUrl,
  isTranscriptJsonLocation,
} from '@/shared/functions';
import { detachCaptureVideo, loadSameOriginVideoFrame } from '@/shared/loadSameOriginVideoFrame';
import {
  uploadUserImage,
  originBindingFromMedia,
  resolveUserImageUrl,
  resolveUserImageUploadCaseId,
} from '@/shared/userImageStorage';
import { useOptionalToolsSharedCaseData } from '@/components/tools-shared-case-data';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';
import { fetchJunkdrawerPresignedUrl } from '@/shared/cors-junkdrawer-api';
import Controls from './controls';
import Track from './track';
import Transcript from './transcript';
import { MediaSource, Source } from '@/shared/types';
import { useReactiveAuthTokens, useReactiveFeatureFlagValue, FullscreenTransformStateContext } from '@/app/contexts';
import { useDeferredFullscreenZoom } from '@/lib/fullscreen-zoom';

interface TranscriptItem {
  start: number;
  end: number;
  text: string;
}

interface MediaPlayerProps {
  mediaItem: MediaSource;
  transcriptSrc?: string;
  showTranscript?: boolean;
  showTranscriptMarkers?: boolean;
  transcriptPosition?: 'side' | 'below';
  disableHotkeys?: boolean;
  updateMediaContext?: (field: string | object, value?: any) => void;
  useStillFrame?: boolean;
  isAccordion?: boolean;
  isHorizontalTimeline?: boolean;
  isTable?: boolean;
  isEditing?: boolean;
  isUnifiedSources?: boolean;
  isFullscreen?: boolean;
  isPresentation?: boolean;
  hideMediaChrome?: boolean;
  /** When true, pause playback and ignore transport so capture drags don't seek. */
  captureMode?: boolean;
  temporaryRotation?: any;
  sourcesExpanded?: boolean;
  /** Fullscreen fit baseline multiplier; negative values shrink video to free transcript space. */
  zoomLevel?: number;
  /** Stable SourceCard / presentation fit box (preferred over measuring a content-sized slot). */
  fitWidth?: number;
  fitHeight?: number;
  /** UnifiedSources selected-media panel (event modal). */
  isEventModal?: boolean;
  /**
   * Width "credit" for chrome the caller renders alongside this player in the same
   * row (e.g. MediaPane's prev/next carets) that eats into the row's rendered width
   * without being part of the player+transcript content itself. Added back only for
   * the SIDE_TRANSCRIPT_MIN_CONTAINER_WIDTH_PX collapse check — never used for actual
   * video pixel sizing, which already measures the real space it has — so a caller
   * that doesn't pass it simply keeps today's behavior (Bugbot follow-up, TR-1589).
   */
  narrowLayoutWidthAllowancePx?: number;
}

/** UnifiedSources event-modal inline layout when transcript is beside the player. */
export const EVENT_MODAL_SIDE_PLAYER_WIDTH_PX = 376;
export const EVENT_MODAL_SIDE_TRANSCRIPT_WIDTH_PX = 318;
export const EVENT_MODAL_SIDE_LAYOUT_GAP_PX = 16;
export const EVENT_MODAL_SIDE_CONTAINER_WIDTH_PX =
  EVENT_MODAL_SIDE_PLAYER_WIDTH_PX +
  EVENT_MODAL_SIDE_TRANSCRIPT_WIDTH_PX +
  EVENT_MODAL_SIDE_LAYOUT_GAP_PX;

/** Fullscreen audio: 5% padding above/below leaves a 90% content band. */
const FULLSCREEN_MEDIA_VERTICAL_PADDING_RATIO = 0.05;
/**
 * Default below-stacked transcript panel height. Must stay in sync with
 * ``DEFAULT_BELOW_TRANSCRIPT_HEIGHT_PX`` in transcript.tsx — video fit reserves
 * and the panel's own height both use this, or the drag handle is clipped.
 */
const TRANSCRIPT_MIN_HEIGHT_PX = 320;
const FULLSCREEN_LAYOUT_GAP_PX = 16;
/**
 * Used when in-root `.mediaDisplayControls` is not mounted yet. Fullscreen
 * capture uses a Pane header (36px) plus the Actions bar (52px) *outside*
 * `.mediaDisplay`, so the live measure returns 0 in that layout.
 */
const FULLSCREEN_TOOLBAR_HEIGHT_FALLBACK_PX = 88;
/**
 * Track + Controls under the video (fallback when the live shell cannot be
 * measured yet). Prefer {@link measureSoftBelowChromePx}.
 */
const FULLSCREEN_PLAYER_CHROME_HEIGHT_PX = 96;
/** Fullscreen below-stack: borders, rounding, and toolbar/slot measure lag. */
const FULLSCREEN_BELOW_STACK_SLACK_PX = 12;
const FULLSCREEN_SIDE_TRANSCRIPT_WIDTH_RESERVE_PX = 266;
/** Below this available width, a side-by-side transcript has no room to breathe next
 *  to the player — collapse to a below-stacked layout instead of cramming both into
 *  an unreadably narrow column. Applies uniformly to fullscreen, presentation, event
 *  modal, and regular event editing since they all funnel through this one component
 *  (Nic follow-up, TR-1589). */
const SIDE_TRANSCRIPT_MIN_CONTAINER_WIDTH_PX = 600;
/** Same as TRANSCRIPT_MIN_HEIGHT_PX — named for the presentation reserve call site. */
const PRESENTATION_BELOW_TRANSCRIPT_RESERVE_PX = TRANSCRIPT_MIN_HEIGHT_PX;
/**
 * Opaque inverse plate behind the video. A bare ``var(--surface-inverse)``
 * paints transparent because theme-core is not loaded globally in this app,
 * and ``bg-black/50`` lets the slide copy show through the preview (TR-3842).
 */
const MEDIA_PREVIEW_SURFACE_BG = 'bg-[var(--surface-inverse,#0f1d32)]';
const PRESENTATION_PLAYER_CHROME_RESERVE_PX = FULLSCREEN_PLAYER_CHROME_HEIGHT_PX;
/** Floor when soft-fitting a below transcript into a short SourceCard scrollport. */
const SOFT_BELOW_TRANSCRIPT_MIN_PX = BELOW_STACK_TRANSCRIPT_MIN_PX;
const SOFT_BELOW_VIDEO_MIN_PX = BELOW_STACK_VIDEO_MIN_PX;
/** Sub-pixel / border rounding only. */
const SOFT_BELOW_STACK_SLACK_PX = 2;
/**
 * Ignore 1px chrome / scrollbar / sub-pixel churn when committing soft-fit or
 * side-transcript heights. Without this, zoom-out (transcript absorbs slack) can
 * leave a ResizeObserver ↔ soft-fit loop that jitters the transcript bottom edge.
 */
const LAYOUT_HEIGHT_HYSTERESIS_PX = 2;

/** Marked on the Evidence Locker preview scrollport in question-bar.tsx. */
function isEvidenceLockerScrollport(scrollport: HTMLElement | null): boolean {
  return !!scrollport?.hasAttribute('data-evidence-locker-preview');
}

function isEvidenceLockerOverlayScrollport(scrollport: HTMLElement | null): boolean {
  return !!scrollport?.hasAttribute('data-evidence-locker-overlay');
}

/**
 * Bottom edge of the soft-fit budget in viewport coordinates.
 *
 * Answer-item: flex-stretched scrollport bottom (capped by ancestor max-heights).
 * Evidence Locker: React Aria inline ``maxHeight`` on the floating root (placement),
 * then the PopoverContent painted bottom once that root is at its cap — CSS
 * ``75vh`` alone overshoots the real popover border by ~20px+.
 */
function measureSoftClipBottom(containerEl: HTMLElement): number {
  const scrollport = containerEl.closest(
    '[data-source-card-scrollport]',
  ) as HTMLElement | null;
  const isEL = isEvidenceLockerScrollport(scrollport);

  let visibleBottom = window.innerHeight;
  let ariaCapBottom = Number.POSITIVE_INFINITY;

  if (scrollport && !isEL) {
    // Answer-item: flex-stretched column — use the sources-nav sibling's height, not
    // the flex row's cross size (includes our column and grows with CSV / long viewers).
    const portRect = scrollport.getBoundingClientRect();
    let capBottom = portRect.bottom;
    const row = scrollport.parentElement;
    if (row) {
      const rowStyle = getComputedStyle(row);
      if (
        rowStyle.display === 'flex'
        && (rowStyle.flexDirection === 'row' || rowStyle.flexDirection === 'row-reverse')
      ) {
        for (const child of Array.from(row.children)) {
          if (child === scrollport || !(child instanceof HTMLElement)) continue;
          const siblingRect = child.getBoundingClientRect();
          if (siblingRect.height > 0) {
            capBottom = Math.max(capBottom, siblingRect.bottom);
          }
          break;
        }
      }
    }
    const portMaxH = Number.parseFloat(getComputedStyle(scrollport).maxHeight);
    if (Number.isFinite(portMaxH) && portMaxH > 0) {
      capBottom = Math.min(capBottom, portRect.top + portMaxH);
    }
    visibleBottom = Math.min(visibleBottom, capBottom);
  }

  let node: HTMLElement | null = scrollport?.parentElement ?? containerEl.parentElement;
  while (node && node !== document.documentElement) {
    const rect = node.getBoundingClientRect();
    const computedMax = Number.parseFloat(getComputedStyle(node).maxHeight);
    if (Number.isFinite(computedMax) && computedMax > 0) {
      visibleBottom = Math.min(visibleBottom, rect.top + computedMax);
    }
    // React Aria writes placement maxHeight as an inline style on the overlay.
    if (node.style?.maxHeight) {
      const inlineMax = Number.parseFloat(node.style.maxHeight);
      if (Number.isFinite(inlineMax) && inlineMax > 0) {
        const cap = rect.top + inlineMax;
        ariaCapBottom = Math.min(ariaCapBottom, cap);
        visibleBottom = Math.min(visibleBottom, cap);
        if (rect.height >= inlineMax - 1) {
          visibleBottom = Math.min(visibleBottom, rect.bottom);
        }
      }
    }
    node = node.parentElement;
  }

  if (isEL && scrollport) {
    const popover = scrollport.parentElement;
    if (popover) {
      const popRect = popover.getBoundingClientRect();
      // Only trust the painted bottom when the popover has reached its Aria cap;
      // while content-sized/short it would under-measure and shrink the video.
      if (
        Number.isFinite(ariaCapBottom) &&
        popRect.bottom >= ariaCapBottom - 2
      ) {
        visibleBottom = Math.min(visibleBottom, popRect.bottom);
      } else if (Number.isFinite(ariaCapBottom)) {
        visibleBottom = Math.min(visibleBottom, ariaCapBottom);
      } else {
        visibleBottom = Math.min(visibleBottom, popRect.bottom);
      }
    }
  }

  return visibleBottom;
}

function measureSoftBelowChromePx(
  playerEl: HTMLElement | null,
  videoBoxEl: HTMLElement | null,
): number {
  if (playerEl && videoBoxEl) {
    const chrome = Math.round(
      playerEl.getBoundingClientRect().height - videoBoxEl.getBoundingClientRect().height,
    );
    if (chrome >= 40) return chrome;
  }
  return PRESENTATION_PLAYER_CHROME_RESERVE_PX;
}

/**
 * Chrome actually inside the player block, for a caller holding a measured stack total
 * that already accounts for it. Unlike {@link measureSoftBelowChromePx} this never
 * substitutes the reserve: with no video box at all (audio) the whole block is chrome,
 * and ``null`` is returned only while a video box that does exist has yet to be laid
 * out, where nothing can be inferred.
 */
function measureExactBelowChromePx(
  playerEl: HTMLElement | null,
  videoBoxEl: HTMLElement | null,
): number | null {
  if (!playerEl) return null;
  const playerH = Math.round(playerEl.getBoundingClientRect().height);
  if (!(playerH > 0)) return null;
  if (!videoBoxEl) return playerH;
  const videoH = videoBoxEl.getBoundingClientRect().height;
  if (!(videoH > 0)) return null;
  return Math.max(0, playerH - Math.round(videoH));
}

/** Player block + split bar + transcript panel (below-stack outer height). */
function measureBelowStackOuterPx(
  playerEl: HTMLElement | null,
  transcriptEl: HTMLElement | null,
  transcriptHeightFallbackPx?: number,
): number {
  if (!playerEl) return 0;
  const playerBlockH = Math.round(playerEl.getBoundingClientRect().height);
  const measuredTranscriptH = transcriptEl
    ? Math.round(transcriptEl.getBoundingClientRect().height)
    : 0;
  // Fall back whenever the panel is not measurable, not just when it is absent: a 0
  // rect mid-layout would otherwise discard a resize the caller already applied.
  const transcriptH =
    measuredTranscriptH > 0
      ? measuredTranscriptH
      : Math.round(transcriptHeightFallbackPx ?? 0);
  if (playerBlockH <= 0 || transcriptH <= 0) return 0;
  return playerBlockH + BELOW_STACK_SPLITTER_TOTAL_PX + transcriptH;
}

/**
 * Split a SourceCard / Evidence Locker scrollport budget between video frame and
 * below-transcript panel so the drag handle stays in the first viewport.
 */
function fitSoftBelowStack(
  availableHeight: number,
  chromePx: number = PRESENTATION_PLAYER_CHROME_RESERVE_PX,
  transcriptOverridePx?: number | null,
  /**
   * Treat the override as a committed floor instead of clamping it to protect the
   * video minimum. Set only for a height the user chose with the bottom grip: every
   * input here is an estimate (chrome falls back to a reserve, the shell may not be
   * measurable yet), and an estimate must not undo an explicit resize. Any resulting
   * overflow is what the scrollport scrolls, and the Evidence Locker tightens its
   * popover ceiling on the next paint.
   */
  honorOverrideAsFloor = false,
): {
  videoCap: number;
  transcriptH: number;
} {
  const stackSlackPx =
    transcriptOverridePx != null ? 0 : SOFT_BELOW_STACK_SLACK_PX;
  const usable = Math.max(
    0,
    availableHeight
      - chromePx
      - BELOW_STACK_SPLITTER_TOTAL_PX
      - stackSlackPx,
  );
  if (
    transcriptOverridePx != null
    && Number.isFinite(transcriptOverridePx)
    && transcriptOverridePx > 0
  ) {
    const requested = Math.max(
      SOFT_BELOW_TRANSCRIPT_MIN_PX,
      Math.round(transcriptOverridePx),
    );
    const transcriptH = honorOverrideAsFloor
      ? requested
      : Math.min(
        requested,
        Math.max(SOFT_BELOW_TRANSCRIPT_MIN_PX, usable - SOFT_BELOW_VIDEO_MIN_PX),
      );
    const videoCap = Math.max(SOFT_BELOW_VIDEO_MIN_PX, usable - transcriptH);
    return { videoCap, transcriptH };
  }
  let transcriptH = Math.min(
    PRESENTATION_BELOW_TRANSCRIPT_RESERVE_PX,
    Math.max(SOFT_BELOW_TRANSCRIPT_MIN_PX, usable - SOFT_BELOW_VIDEO_MIN_PX),
  );
  if (transcriptH + SOFT_BELOW_VIDEO_MIN_PX > usable) {
    transcriptH = Math.max(SOFT_BELOW_TRANSCRIPT_MIN_PX, usable - SOFT_BELOW_VIDEO_MIN_PX);
  }
  const videoCap = Math.max(SOFT_BELOW_VIDEO_MIN_PX, usable - transcriptH);
  return { videoCap, transcriptH };
}

/** Zoom-1 layout snapshot for below-stacked player + transcript. */
type BelowStackZoomBaseline = {
  videoFramePx: number;
  transcriptPx: number;
  stackOuterPx: number;
  chromePx: number;
  baseWidth: number;
};

const ZOOM_ONE_EPSILON = 0.001;

function isZoomScaleOne(zoomScale: number): boolean {
  return Math.abs(zoomScale - 1) < ZOOM_ONE_EPSILON;
}

function capVideoFrameToColumnWidth(
  videoFramePx: number,
  videoRatio: number,
  baseWidth: number,
): number {
  if (!(videoRatio > 0) || videoFramePx <= 0) {
    return videoFramePx;
  }
  let finalH = videoFramePx;
  let finalW = Math.round(finalH * videoRatio);
  if (baseWidth > 0 && finalW > baseWidth) {
    finalW = baseWidth;
    finalH = Math.round(finalW / videoRatio);
  }
  return finalH;
}

/**
 * Non-fullscreen zoom: scale video height from baseline; width stops at the
 * column edge and does not grow past it.
 */
function scaleInlineVideoFrame(
  baselineVideoFramePx: number,
  zoomScale: number,
  videoRatio: number,
  columnWidthPx: number,
): { videoFramePx: number; videoWidthPx: number } {
  const videoFramePx = Math.max(
    SOFT_BELOW_VIDEO_MIN_PX,
    Math.round(baselineVideoFramePx * zoomScale),
  );
  let videoWidthPx = Math.round(videoFramePx * videoRatio);
  if (columnWidthPx > 0 && videoWidthPx > columnWidthPx) {
    videoWidthPx = columnWidthPx;
  }
  return { videoFramePx, videoWidthPx };
}

/**
 * Below-stacked SourceCard / inline zoom — zoom-in grows the outer stack by the
 * same delta as the video frame; zoom-out shrinks the frame only (transcript
 * absorbs slack) and never reduces the stack height reached during zoom-in.
 */
function applyInlineBelowStackZoom(
  baseline: BelowStackZoomBaseline,
  zoomScale: number,
  videoRatio: number,
  pinnedStackOuterPx: number | null,
): {
  videoFramePx: number;
  videoWidthPx: number;
  transcriptPx: number;
  stackOuterPx: number;
} {
  const { videoFramePx, videoWidthPx } = scaleInlineVideoFrame(
    baseline.videoFramePx,
    zoomScale,
    videoRatio,
    baseline.baseWidth,
  );
  const floorStackOuterPx = Math.max(
    pinnedStackOuterPx ?? 0,
    baseline.stackOuterPx,
  );

  if (zoomScale > 1 + ZOOM_ONE_EPSILON) {
    const stackOuterPx = Math.max(
      floorStackOuterPx,
      baseline.stackOuterPx + (videoFramePx - baseline.videoFramePx),
    );
    return {
      videoFramePx,
      videoWidthPx,
      transcriptPx: baseline.transcriptPx,
      stackOuterPx,
    };
  }

  if (zoomScale < 1 - ZOOM_ONE_EPSILON) {
    const stackOuterPx = floorStackOuterPx;
    const maxVideoPx = Math.max(
      SOFT_BELOW_VIDEO_MIN_PX,
      stackOuterPx
        - BELOW_STACK_SPLITTER_TOTAL_PX
        - baseline.chromePx
        - SOFT_BELOW_TRANSCRIPT_MIN_PX,
    );
    const clampedVideoPx = Math.min(videoFramePx, maxVideoPx);
    const clampedWidthPx =
      baseline.baseWidth > 0
        ? Math.min(baseline.baseWidth, Math.round(clampedVideoPx * videoRatio))
        : Math.round(clampedVideoPx * videoRatio);
    const transcriptPx = stackOuterPx
      - BELOW_STACK_SPLITTER_TOTAL_PX
      - baseline.chromePx
      - clampedVideoPx;
    return {
      videoFramePx: clampedVideoPx,
      videoWidthPx: clampedWidthPx,
      transcriptPx: Math.max(SOFT_BELOW_TRANSCRIPT_MIN_PX, transcriptPx),
      stackOuterPx,
    };
  }

  if (floorStackOuterPx > baseline.stackOuterPx) {
    const transcriptPx = floorStackOuterPx
      - BELOW_STACK_SPLITTER_TOTAL_PX
      - baseline.chromePx
      - baseline.videoFramePx;
    return {
      videoFramePx: baseline.videoFramePx,
      videoWidthPx: Math.min(
        baseline.baseWidth,
        Math.round(baseline.videoFramePx * videoRatio),
      ),
      transcriptPx: Math.max(SOFT_BELOW_TRANSCRIPT_MIN_PX, transcriptPx),
      stackOuterPx: floorStackOuterPx,
    };
  }

  return {
    videoFramePx: baseline.videoFramePx,
    videoWidthPx: Math.min(
      baseline.baseWidth,
      Math.round(baseline.videoFramePx * videoRatio),
    ),
    transcriptPx: baseline.transcriptPx,
    stackOuterPx: baseline.stackOuterPx,
  };
}

/** Fixed outer stack: transcript grows/shrinks opposite the video frame. */
function rebalanceBelowStackForFixedOuter(
  stackOuterPx: number,
  chromePx: number,
  videoFramePx: number,
  transcriptPx: number,
  transcriptMinPx: number = SOFT_BELOW_TRANSCRIPT_MIN_PX,
): { videoFramePx: number; transcriptPx: number } {
  const maxTranscript = Math.max(
    transcriptMinPx,
    stackOuterPx
      - BELOW_STACK_SPLITTER_TOTAL_PX
      - chromePx
      - SOFT_BELOW_VIDEO_MIN_PX,
  );
  let nextTranscript = Math.max(
    transcriptMinPx,
    Math.min(maxTranscript, Math.round(transcriptPx)),
  );
  let nextVideo = stackOuterPx
    - BELOW_STACK_SPLITTER_TOTAL_PX
    - chromePx
    - nextTranscript;
  nextVideo = Math.max(SOFT_BELOW_VIDEO_MIN_PX, nextVideo);
  nextTranscript = stackOuterPx
    - BELOW_STACK_SPLITTER_TOTAL_PX
    - chromePx
    - nextVideo;
  return { videoFramePx: nextVideo, transcriptPx: nextTranscript };
}

/**
 * Fullscreen below-stack zoom: fixed outer height; video takes space from the
 * transcript down to ``transcriptMinPx`` (150px). Stack reallocation ensures
 * zoom-in still grows the frame when the column width is already saturated.
 */
function rebalanceFullscreenBelowZoom(
  baseline: BelowStackZoomBaseline,
  zoomScale: number,
  videoRatio: number,
  transcriptMinPx: number,
): { videoFramePx: number; transcriptPx: number } {
  const stackContent =
    baseline.stackOuterPx
    - BELOW_STACK_SPLITTER_TOTAL_PX
    - baseline.chromePx;
  const minVideo = SOFT_BELOW_VIDEO_MIN_PX;
  const maxVideo = Math.max(minVideo, stackContent - transcriptMinPx);

  let shiftPx = 0;
  if (zoomScale > 1 + ZOOM_ONE_EPSILON) {
    const flexTranscript = Math.max(0, baseline.transcriptPx - transcriptMinPx);
    shiftPx = Math.min(
      flexTranscript,
      Math.max(0, Math.round(baseline.videoFramePx * (zoomScale - 1))),
    );
  } else if (zoomScale < 1 - ZOOM_ONE_EPSILON) {
    shiftPx = -Math.min(
      baseline.videoFramePx - minVideo,
      Math.max(0, Math.round(baseline.videoFramePx * (1 - zoomScale))),
    );
  }

  let targetVideo = baseline.videoFramePx + shiftPx;
  targetVideo = capVideoFrameToColumnWidth(targetVideo, videoRatio, baseline.baseWidth);
  if (zoomScale > 1 + ZOOM_ONE_EPSILON && shiftPx > 0) {
    // Width cap must not block stack reallocation on zoom-in.
    targetVideo = Math.max(targetVideo, Math.min(maxVideo, baseline.videoFramePx + shiftPx));
    targetVideo = capVideoFrameToColumnWidth(targetVideo, videoRatio, baseline.baseWidth);
  }
  targetVideo = Math.max(minVideo, Math.min(maxVideo, targetVideo));

  let targetTranscript = stackContent - targetVideo;
  targetTranscript = Math.max(transcriptMinPx, targetTranscript);
  targetVideo = Math.max(minVideo, stackContent - targetTranscript);

  return { videoFramePx: targetVideo, transcriptPx: stackContent - targetVideo };
}

function getSourceCardPaddingBottom(containerEl: HTMLElement): number {
  const card = containerEl.closest('[data-testid="SourceCard"]') as HTMLElement | null;
  if (!card) return 0;
  const padBottom = Number.parseFloat(getComputedStyle(card).paddingBottom);
  return Number.isFinite(padBottom) && padBottom > 0 ? padBottom : 0;
}

/**
 * Soft vertical budget for SourceCard / Evidence Locker when there is no locked
 * ``fitHeight``.
 *
 * Stops at the SourceCard inner bottom (above ``padding-bottom``) so the stack
 * does not plan for space the card chrome owns. Answer-item pairs this with
 * grow-to-fill so subtracting padding does not leave a visible gap.
 */
function measureSoftBelowTranscriptBudget(containerEl: HTMLElement | null): number {
  if (!containerEl || typeof window === 'undefined') return 0;

  const elTop = containerEl.getBoundingClientRect().top;
  let visibleBottom = measureSoftClipBottom(containerEl);

  const scrollport = containerEl.closest(
    '[data-source-card-scrollport]',
  ) as HTMLElement | null;
  if (scrollport) {
    visibleBottom -= getSourceCardPaddingBottom(containerEl);
    if (!isEvidenceLockerScrollport(scrollport)) {
      const portPadBottom = Number.parseFloat(getComputedStyle(scrollport).paddingBottom);
      if (Number.isFinite(portPadBottom) && portPadBottom > 0) {
        visibleBottom -= portPadBottom;
      }
    }
  }

  return Math.max(0, Math.floor(visibleBottom - elTop));
}

/** Scrollport box fallback when clip math briefly returns 0 (first loadedmetadata). */
function measureScrollportSoftBudgetFallback(containerEl: HTMLElement | null): number {
  if (!containerEl || typeof window === 'undefined') return 0;
  const scrollport = containerEl.closest(
    '[data-source-card-scrollport]',
  ) as HTMLElement | null;
  if (!scrollport) return 0;
  const portRect = scrollport.getBoundingClientRect();
  if (!(portRect.height > 0)) return 0;
  const elTop = containerEl.getBoundingClientRect().top;
  let bottom = portRect.bottom;
  bottom -= getSourceCardPaddingBottom(containerEl);
  if (!isEvidenceLockerScrollport(scrollport)) {
    const portPadBottom = Number.parseFloat(getComputedStyle(scrollport).paddingBottom);
    if (Number.isFinite(portPadBottom) && portPadBottom > 0) {
      bottom -= portPadBottom;
    }
  }
  return Math.max(0, Math.floor(bottom - elTop));
}

/** Fullscreen only — `.mediaDisplayControls` is taller than the old 44px estimate. */
function measureFullscreenToolbarHeightPx(containerEl: HTMLElement | null): number {
  const mediaDisplay = containerEl?.closest('.mediaDisplay') as HTMLElement | null;
  const toolbar = mediaDisplay?.querySelector('.mediaDisplayControls') as HTMLElement | null;
  if (toolbar) {
    const measured = Math.round(toolbar.getBoundingClientRect().height);
    if (measured > 0) return measured;
  }
  const inner = document.getElementById('fullScreenMediaDisplayInner');
  // Pane header + Actions sit outside `.mediaDisplay`. Do not subtract a
  // leftover toolbar guess from a root that already excludes them.
  if (inner && mediaDisplay && inner.contains(mediaDisplay) && !toolbar) {
    return 0;
  }
  return FULLSCREEN_TOOLBAR_HEIGHT_FALLBACK_PX;
}

/** Fullscreen only — flex-1 slot under `.mediaDisplayControls` (not inner − toolbar guess). */
function measureFullscreenPlayerBudgetPx(containerEl: HTMLElement | null): number {
  if (!containerEl) return 0;
  const slot =
    (containerEl.closest('#media-player-container-fullscreen') as HTMLElement | null)
    ?? (containerEl.parentElement as HTMLElement | null);
  if (slot) {
    const h = Math.floor(slot.getBoundingClientRect().height);
    if (h > 0) return h;
  }
  const { height } = measureMediaDisplayContainer(containerEl);
  if (height > 0) {
    return Math.max(0, height - measureFullscreenToolbarHeightPx(containerEl));
  }
  return 0;
}

function getFullscreenBelowChromePx(
  playerEl: HTMLElement | null,
  videoBoxEl: HTMLElement | null,
): number {
  return measureSoftBelowChromePx(playerEl, videoBoxEl);
}

function reserveFullscreenBelowVideoFrameHeight(
  playerBudgetPx: number,
  transcriptReservePx: number,
  chromePx: number = FULLSCREEN_PLAYER_CHROME_HEIGHT_PX,
): number {
  return Math.max(
    120,
    playerBudgetPx
      - transcriptReservePx
      - BELOW_STACK_SPLITTER_TOTAL_PX
      - chromePx
      - FULLSCREEN_BELOW_STACK_SLACK_PX,
  );
}

function measureMediaDisplayContainer(containerEl: HTMLElement | null): { width: number; height: number } {
  const mediaDisplay = containerEl?.closest('.mediaDisplay') as HTMLElement | null;
  const inner = document.getElementById('fullScreenMediaDisplayInner');
  const inFullscreen = Boolean(inner && containerEl && inner.contains(containerEl));
  // Prefer `.mediaDisplay` in the fullscreen Pane: header + Actions are siblings
  // of that root, so the inner viewport overstates the video/transcript budget.
  const ancestor =
    (inFullscreen && mediaDisplay) ||
    (inFullscreen ? inner : null) ||
    mediaDisplay ||
    containerEl?.parentElement ||
    containerEl;
  if (!ancestor) {
    return { width: 0, height: 0 };
  }
  const rect = ancestor.getBoundingClientRect();
  return { width: Math.round(rect.width), height: Math.round(rect.height) };
}

/** Leave room for transcript + player chrome so the transcript stays reachable. */
function reserveBelowTranscriptFromMaxHeight(
  maxHeight: number,
  transcriptReservePx: number,
): number {
  return maxHeight
    - transcriptReservePx
    - BELOW_STACK_SPLITTER_TOTAL_PX
    - FULLSCREEN_PLAYER_CHROME_HEIGHT_PX;
}

/**
 * Cap on how many times we'll re-mint a presigned URL after an HTML5 media
 * ``error`` event before bailing out. Prevents an infinite refetch loop on
 * permanent failures (404, codec, etc.); a fresh ``onLoadedData`` resets the
 * counter so a long viewing session can still recover from multiple expiries.
 */
const MAX_PRESIGN_REFRESH_ATTEMPTS = 2;

const isDataUrlImage = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('data:image/');

const isLegacyStillFrameTimeIndex = (value: unknown): value is string =>
  typeof value === 'string' && /^\d+(?::\d+)*$/.test(value.trim());

const getStillFrameImage = (mediaItem: MediaSource | null | undefined): string | undefined => {
  for (const context of [mediaItem?.mediaContext, mediaItem?.sourceContext]) {
    if (isDataUrlImage(context?.stillFrame)) {
      return context.stillFrame;
    }
    if (isDataUrlImage(context?.canvas?.stillFrameImage)) {
      return context.canvas.stillFrameImage;
    }
    const storedRef = context?.canvas?.stillFrameImage;
    if (typeof storedRef === 'string' && storedRef.trim() !== '') {
      return storedRef.trim();
    }
  }
  return undefined;
};

const captureVideoFrame = (video: HTMLVideoElement): string | null => {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) {
    return null;
  }

  const maxStillFrameSize = 320;
  const scale = Math.min(1, maxStillFrameSize / Math.max(width, height));
  const canvasWidth = Math.round(width * scale);
  const canvasHeight = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);
  return canvas.toDataURL('image/webp');
};

const MediaPlayer: React.FC<MediaPlayerProps> = ({
  mediaItem,
  transcriptSrc,
  showTranscript = true,
  showTranscriptMarkers = false,
  transcriptPosition: providedTranscriptPosition,
  disableHotkeys,
  updateMediaContext,
  useStillFrame,
  isAccordion,
  isHorizontalTimeline,
  isTable,
  isEditing = false,
  isUnifiedSources,
  isFullscreen = false,
  isPresentation = false,
  hideMediaChrome = false,
  captureMode = false,
  temporaryRotation,
  sourcesExpanded,
  zoomLevel = 0,
  fitWidth,
  fitHeight,
  isEventModal = false,
  narrowLayoutWidthAllowancePx = 0,
}) => {
  const t = useTimePilotT();
  const fullscreenTransform = useContext(FullscreenTransformStateContext);
  const deferredFullscreenZoom = useDeferredFullscreenZoom(isFullscreen, zoomLevel);
  const effectiveZoomLevel = deferredFullscreenZoom.layoutLevel;
  const effectiveTemporaryRotation = isFullscreen ? fullscreenTransform.temporaryRotation : temporaryRotation;
  const mediaRef = useRef<HTMLAudioElement | HTMLVideoElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(-1);
  const [transcript, setTranscript] = useState<TranscriptItem[]>([]);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const refreshAttemptsRef = useRef(0);
  const [showTranscriptState, setShowTranscriptState] = useState(showTranscript);
  const [showTranscriptMarkersState, setShowTranscriptMarkersState] = useState(showTranscriptMarkers);
  const [transcriptText, setTranscriptText] = useState<string | undefined>(undefined);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredTranscript, setFilteredTranscript] = useState<TranscriptItem[]>([]);
  const [isSearchInputVisible, setIsSearchInputVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingMediaContext, setIsEditingMediaContext] = useState(false);
  const [hasManuallyOverriddenEndTime, setHasManuallyOverriddenEndTime] = useState(false);
  const hasAppliedStartTimeRef = useRef(false);
  const isAudioFile = getMediaType(mediaItem, mediaItem.fileInfo ?? null) === 'audio';
  const isCompactHorizontalAudio = isHorizontalTimeline && isAudioFile;
  const defaultTranscriptPosition = isAudioFile ? 'side' : 'side';
  const [isPortrait, setIsPortrait] = useState(false);
  const [transcriptPosition, setTranscriptPosition] = useState<'side' | 'below'>(
    providedTranscriptPosition || defaultTranscriptPosition
  );
  const [videoStyle, setVideoStyle] = useState<React.CSSProperties>(() => (
    isPresentation && !isFullscreen
      ? {
          // Always span the SourceCard column; height follows intrinsic AR.
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          rotate: `${mediaItem?.mediaContext?.rotation || 0}deg`,
        }
      : {
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          rotate: `${mediaItem?.mediaContext?.rotation || 0}deg`,
        }
  ));
  const [videoCentererStyle, setVideoCentererStyle] = useState<React.CSSProperties>(() => (
    isPresentation && !isFullscreen
      ? {
          width: '100%',
          maxWidth: '100%',
          height: 'auto',
          flexShrink: 0,
          overflow: 'hidden',
        }
      : {
          width: '100%',
          height: 'auto',
          overflow: 'hidden',
        }
  ));
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [fullscreenBelowTranscriptHeight, setFullscreenBelowTranscriptHeight] = useState<number | null>(null);
  /**
   * Below-transcript panel height chosen by soft scrollport fit (SourceCard /
   * Evidence Locker). Kept in state so the transcript panel matches the video
   * reserve instead of always using the 320px default (which overflows short
   * answer-item scrollports).
   */
  const [softBelowTranscriptPanelHeight, setSoftBelowTranscriptPanelHeight] = useState<number | null>(null);
  /** User drag on the between-player/transcript split bar (fixed outer stack height). */
  const [belowStackTranscriptPx, setBelowStackTranscriptPx] = useState<number | null>(null);
  const [belowStackSplitDragging, setBelowStackSplitDragging] = useState(false);
  const [belowStackPinnedOuterHeightPx, setBelowStackPinnedOuterHeightPx] = useState<number | null>(null);
  /** Side-layout inline zoom: player shell min-height never shrinks after zoom-in. */
  const [pinnedPresentationPlayerOuterPx, setPinnedPresentationPlayerOuterPx] = useState<number | null>(null);
  const pinnedPresentationPlayerOuterRef = useRef<number | null>(null);
  const belowStackSplitLockedRef = useRef(false);
  const belowStackSplitDragActiveRef = useRef(false);
  /** Bottom transcript grip is down (drag lives in `Transcript`, not here). */
  const belowGripDragActiveRef = useRef(false);
  const pinnedBelowStackOuterHeightRef = useRef<number | null>(null);
  /** Player chrome (track + controls) measured once at split drag start. */
  const belowStackChromePxRef = useRef(PRESENTATION_PLAYER_CHROME_RESERVE_PX);
  const belowStackMaxTranscriptPxRef = useRef(SOFT_BELOW_TRANSCRIPT_MIN_PX);
  const belowStackDragTranscriptPxRef = useRef<number | null>(null);
  /** Live split layout during drag — survives currentTime re-renders. */
  const belowStackSplitLayoutRef = useRef<{
    transcriptPx: number;
    centerer: React.CSSProperties;
    video: React.CSSProperties;
  } | null>(null);
  /** User drag on the side-by-side player/transcript split bar (fixed outer row width). */
  const [sideStackTranscriptPx, setSideStackTranscriptPx] = useState<number | null>(null);
  /**
   * Audio side split as the player's share of the row (0–1). Null means an even
   * split. A ratio rather than pixels so both columns keep their minimums when
   * the row later resizes.
   */
  const [audioSideSplitRatio, setAudioSideSplitRatio] = useState<number | null>(null);
  /** Player-column height held only while an audio side drag is active. */
  const [audioSideDragMinHeightPx, setAudioSideDragMinHeightPx] = useState<number | null>(null);
  const [sideStackSplitDragging, setSideStackSplitDragging] = useState(false);
  const [sideStackPinnedOuterWidthPx, setSideStackPinnedOuterWidthPx] = useState<number | null>(null);
  const sideStackSplitLockedRef = useRef(false);
  const sideStackSplitDragActiveRef = useRef(false);
  const pinnedSideStackOuterWidthRef = useRef<number | null>(null);
  const sideStackMaxTranscriptPxRef = useRef(SIDE_TRANSCRIPT_MIN_WIDTH_PX);
  const sideStackDragTranscriptPxRef = useRef<number | null>(null);
  const sideStackChromePxRef = useRef(PRESENTATION_PLAYER_CHROME_RESERVE_PX);
  /** Horizontal space between the player and transcript at drag start (splitter + gap). */
  const sideStackRowGapPxRef = useRef(SIDE_STACK_SPLITTER_TOTAL_PX);
  const sideStackSplitLayoutRef = useRef<{
    transcriptPx: number;
    centerer: React.CSSProperties;
    video: React.CSSProperties;
  } | null>(null);
  /** Zoom-1 below-stack snapshot (SourceCard / fullscreen below transcript). */
  const belowStackZoomBaselineRef = useRef<BelowStackZoomBaseline | null>(null);
  const belowStackZoomBaselineWidthRef = useRef(0);
  /**
   * Last CSS-constraint soft budget (fallback when a measure briefly returns 0).
   * Cleared on media identity / width change.
   */
  const pinnedSoftBelowBudgetRef = useRef<number | null>(null);
  const pinnedSoftBelowBudgetWidthRef = useRef<number>(0);
  /**
   * Upper bound from post-layout overflow correction (Evidence Locker handle past
   * the painted scrollport). Applied as ``min(measured, ceil)`` so AnswerItem can
   * still grow when its CSS max-height budget is larger.
   */
  const softBelowOverflowCeilRef = useRef<number | null>(null);
  /**
   * Transcript height the user left with the bottom grip. Feeds soft-fit as the
   * transcript share so a refit keeps that height instead of recomputing the
   * default reserve split. Cleared wherever the soft budget pin is.
   */
  const userGripBelowTranscriptPxRef = useRef<number | null>(null);
  /**
   * Below-stack outer height committed with the bottom grip. Floors the Evidence
   * Locker soft budget, which re-measures the scrollport on every refit and would
   * otherwise overwrite the ratcheted pin with a smaller pre-expansion value —
   * ``softBelowOverflowCeilRef`` is an upper bound and cannot lift it back.
   * ``softBelowOverflowCeilRef`` still caps this, so the popover-clip correction
   * remains the authority on what the Evidence Locker can actually paint.
   * Cleared wherever the soft budget pin is.
   */
  const userGripSoftBelowBudgetPxRef = useRef<number | null>(null);
  /**
   * Player chrome measured when the grip was released, i.e. in a settled layout. A
   * refit can run while the shell is unmeasurable, where the reserve fallback would
   * subtract chrome the committed stack total already contains.
   */
  const userGripBelowChromePxRef = useRef<number | null>(null);
  /** Guards loadedmetadata deferrals when scrollport budget is not ready yet. */
  const softBelowMetadataRefitCountRef = useRef(0);
  // Live "is there room for beside" signal, independent of the saved/resolved
  // transcriptPosition preference — see SIDE_TRANSCRIPT_MIN_CONTAINER_WIDTH_PX.
  const [isNarrowForSideTranscript, setIsNarrowForSideTranscript] = useState(false);
  // The value everything actually renders with: a saved/resolved 'side' preference
  // collapses to 'below' while the measured container is too narrow to show both
  // side by side. Purely a rendering decision — never written back to mediaContext,
  // so widening the window later snaps straight back to 'side' (Nic follow-up,
  // TR-1589). Declared this early so the video fit-box math further down (which
  // needs to know if a side transcript will actually claim width) sees it too.
  const layoutTranscriptPosition: 'side' | 'below' =
    transcriptPosition === 'side' && isNarrowForSideTranscript ? 'below' : transcriptPosition;
  const [totalRotation, setTotalRotation] = useState<number | undefined>(mediaItem?.mediaContext?.rotation);
  const [stillFrameCentererStyle, setStillFrameCentererStyle] = useState({ width: '100%', height: '100%', overflow: 'hidden' });
  const [trimDragActive, setTrimDragActive] = useState(false);
  const stillFrameImage = getStillFrameImage(mediaItem);
  const stillFrameDisplaySrc = stillFrameImage ? resolveUserImageUrl(stillFrameImage) : undefined;
  const captureRef =
    typeof mediaItem?.mediaContext?.capture === 'string'
      ? mediaItem.mediaContext.capture.trim()
      : '';
  const stillFrameIsCaptureBake =
    Boolean(captureRef) &&
    typeof stillFrameImage === 'string' &&
    stillFrameImage.trim() === captureRef;

  const rectRef = useRef<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoCentererRef = useRef<HTMLDivElement | null>(null);
  const stillFrameCentererRef = useRef<HTMLDivElement | null>(null);
  const dimensionsLockRef = useRef(false);
  /** Freeze video fit-box recalculation while trim handles are dragged. */
  const trimDragActiveRef = useRef(false);
  const stillFrameRef = useRef<HTMLImageElement | null>(null);
  trimDragActiveRef.current = trimDragActive;
  belowStackSplitDragActiveRef.current = belowStackSplitDragging;
  sideStackSplitDragActiveRef.current = sideStackSplitDragging;

  const getEffectiveBelowTranscriptHeight = useCallback((): number => {
    if (belowStackSplitDragging && belowStackDragTranscriptPxRef.current != null) {
      return belowStackDragTranscriptPxRef.current;
    }
    if (belowStackSplitLockedRef.current && belowStackTranscriptPx != null) {
      return belowStackTranscriptPx;
    }
    if (softBelowTranscriptPanelHeight != null && softBelowTranscriptPanelHeight > 0) {
      return softBelowTranscriptPanelHeight;
    }
    if (fullscreenBelowTranscriptHeight != null && fullscreenBelowTranscriptHeight > 0) {
      return fullscreenBelowTranscriptHeight;
    }
    return TRANSCRIPT_MIN_HEIGHT_PX;
  }, [
    belowStackSplitDragging,
    belowStackTranscriptPx,
    softBelowTranscriptPanelHeight,
    fullscreenBelowTranscriptHeight,
  ]);

  /** Commit soft-fit / zoom transcript heights; ignore sub-pixel oscillation. */
  const commitSoftBelowTranscriptPanelHeight = useCallback((next: number | null) => {
    setSoftBelowTranscriptPanelHeight((prev) => {
      if (next == null) {
        return prev == null ? prev : null;
      }
      if (!(next > 0)) {
        return prev;
      }
      if (prev != null && Math.abs(prev - next) < LAYOUT_HEIGHT_HYSTERESIS_PX) {
        return prev;
      }
      return next;
    });
  }, []);

  /** Bottom transcript grip grows the stack; release the between-panel split pin. */
  const unlockBelowStackSplit = useCallback(() => {
    // Same contract as the split drags: the grip owns the panel height while it is
    // down, so a refit must not re-apply soft-fit from the last committed override.
    belowGripDragActiveRef.current = true;
    // Captured here because the layout is settled and the grip only resizes the
    // transcript — the player block, and so its chrome, is the same at release. Gives
    // the commit below a value even if the shell is momentarily unmeasurable then.
    userGripBelowChromePxRef.current =
      measureExactBelowChromePx(playerRef.current, videoCentererRef.current)
      ?? userGripBelowChromePxRef.current;
    belowStackSplitLockedRef.current = false;
    pinnedBelowStackOuterHeightRef.current = null;
    belowStackDragTranscriptPxRef.current = null;
    belowStackSplitLayoutRef.current = null;
    setBelowStackTranscriptPx(null);
    setBelowStackPinnedOuterHeightPx(null);
    if (containerRef.current) {
      containerRef.current.style.height = '';
      containerRef.current.style.maxHeight = '';
      containerRef.current.style.minHeight = '';
      containerRef.current.style.overflow = '';
    }
  }, []);

  /**
   * Bottom transcript grip released — adopt the resulting outer height as the
   * soft-fit budget so a subsequent between-panel split redistributes within the
   * total the user just left, instead of the pre-grip scrollport pin.
   */
  const commitUserResizedBelowStackBudget = useCallback((transcriptHeightPx: number) => {
    // Before the early returns below — a bail-out must not leave refits blocked.
    belowGripDragActiveRef.current = false;
    const playerEl = playerRef.current;
    const transcriptEl = transcriptRef.current;
    if (!playerEl) return;

    const stackOuter = measureBelowStackOuterPx(
      playerEl,
      transcriptEl,
      transcriptHeightPx,
    );
    if (stackOuter <= 0) return;

    // Ratchet only. This budget feeds the video cap / soft-fit `availableHeight`,
    // so lowering it on a shrink removes the headroom the next expansion needs and
    // the grip stops being able to grow the stack.
    pinnedSoftBelowBudgetRef.current = Math.max(
      pinnedSoftBelowBudgetRef.current ?? 0,
      stackOuter,
    );
    // The Evidence Locker branch of soft-fit replaces that pin with a fresh
    // measurement every refit, so the ratchet above only survives in the
    // answer-item. Keep the committed total as a floor for both.
    userGripSoftBelowBudgetPxRef.current = Math.max(
      userGripSoftBelowBudgetPxRef.current ?? 0,
      stackOuter,
    );

    const scrollport = containerRef.current?.closest(
      '[data-source-card-scrollport]',
    ) as HTMLElement | null;
    if (scrollport && isEvidenceLockerScrollport(scrollport)) {
      softBelowOverflowCeilRef.current = Math.max(
        softBelowOverflowCeilRef.current ?? 0,
        stackOuter,
      );
    }

    const transcriptPx = transcriptEl
      ? Math.round(transcriptEl.getBoundingClientRect().height)
      : Math.round(transcriptHeightPx);

    // Hand soft-fit the height the user chose. Clearing this instead would re-run
    // `handleLoadedMetadata` (it is an effect dependency) with no transcript
    // override, which redistributes the ratcheted budget using the default reserve
    // share — the video cap jumps and the grip resize does not stick.
    userGripBelowTranscriptPxRef.current = transcriptPx;
    // Keep the drag-start capture when the shell is not measurable right now.
    userGripBelowChromePxRef.current =
      measureExactBelowChromePx(playerEl, videoCentererRef.current)
      ?? userGripBelowChromePxRef.current;
    setSoftBelowTranscriptPanelHeight(transcriptPx);

    if (belowStackZoomBaselineRef.current) {
      belowStackZoomBaselineRef.current = {
        ...belowStackZoomBaselineRef.current,
        stackOuterPx: stackOuter,
        transcriptPx,
      };
    }
  }, []);

  const orgId = useReactiveFeatureFlagValue('orgId');
  const tenantFriendlyNameFlag = useReactiveFeatureFlagValue('tenantFriendlyName');
  const sharedCaseData = useOptionalToolsSharedCaseData();
  // Reactive token snapshots let the memoized player re-run token-dependent
  // effects (presign / transcript fetches) after Cognito refreshes.
  const { idToken, accessToken, realAccessToken } = useReactiveAuthTokens();

  const handleDownloadWithIntercomFlag = useCallback(
    (source: MediaSource | Source) => {
      const cellId = resolveCellIdFromIdToken(idToken);
      const session = cellId && realAccessToken ? { cellId, accessToken: realAccessToken } : undefined;
      handleDownload(source, session);
    },
    [idToken, realAccessToken],
  );

  useEffect(() => {
    let newTotalRotation = (effectiveTemporaryRotation || 0) + (mediaItem?.mediaContext?.rotation || 0);
    if (newTotalRotation > 270) {
      newTotalRotation = 0;
    } else if (newTotalRotation < 0) {
      newTotalRotation = 270;
    }
    setTotalRotation(newTotalRotation);
  }, [effectiveTemporaryRotation, mediaItem?.mediaContext?.rotation, mediaItem?.mediaContext?.lastUpdated]);

  /**
   * Resolve the underlying S3 key/URL for this ``mediaItem`` in the shape
   * junkdrawer's ``/pre-sign`` route accepts: ``s3://``, bare ``bucket/key``,
   * ``data/bulk/...``, or ``files/...``. Memoized so both the initial fetch
   * effect and the on-error refresh helper see a stable value.
   */
  const objectUrl = useMemo(() => resolveMediaPlaybackObjectUrl(mediaItem), [
    mediaItem?.mediaContext?.previewUrl,
    mediaItem?.mediaSrc,
    mediaItem?.mediaUrl,
    mediaItem?.fileInfo?.previewUrl,
  ]);

  const sameOriginCaptureSrc = useMemo(() => {
    if (!objectUrl) return null;
    return `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(objectUrl)}`;
  }, [objectUrl]);

  // Mint a 4-hour presigned S3 GET URL whenever the underlying media identity
  // or auth context changes. The browser will stream bytes (and Range-seek)
  // directly from S3 against this URL, so there is no per-byte trip through
  // the cell API gateway. Cleanup aborts the in-flight fetch on unmount /
  // mediaItem swap so a stale response can't clobber a newer one.
  // Depend on ``objectUrl`` (not the whole ``mediaItem``) so trim-handle edits to
  // mediaContext do not remint the URL and reload the media element.
  useEffect(() => {
    const cellId = resolveCellIdFromIdToken(idToken);
    if (!cellId || !realAccessToken || !objectUrl) {
      setPresignedUrl(null);
      return;
    }

    const controller = new AbortController();
    refreshAttemptsRef.current = 0;
    fetchJunkdrawerPresignedUrl({
      cellId,
      accessToken: realAccessToken,
      s3Url: objectUrl,
      signal: controller.signal,
    })
      .then((res) => setPresignedUrl(res.presignedUrl))
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('Failed to mint presigned media URL', err);
        setPresignedUrl(null);
      });

    return () => controller.abort();
  }, [objectUrl, idToken, realAccessToken]);

  /**
   * Re-mint a presigned URL on demand (called from ``handleMediaTagError``
   * when the ``<audio>``/``<video>`` element reports an error — typically a
   * 403 because the previous URL aged out of its 4-hour TTL). Returns the
   * fresh URL; the caller is responsible for committing it to
   * ``presignedUrl`` state synchronously (via ``flushSync``) before calling
   * ``.load()`` on the media element, so the DOM ``src`` attribute is fresh
   * when the reload runs. Committing the state here and letting React batch
   * the render is a race — ``.load()`` would re-fetch the expired URL still
   * in the DOM.
   */
  const refreshPresignedUrl = useCallback(async (): Promise<string | null> => {
    const cellId = resolveCellIdFromIdToken(idToken);
    if (!cellId || !realAccessToken || !objectUrl) return null;
    try {
      const res = await fetchJunkdrawerPresignedUrl({
        cellId,
        accessToken: realAccessToken,
        s3Url: objectUrl,
      });
      return res.presignedUrl;
    } catch (err) {
      console.error('Failed to refresh presigned media URL', err);
      return null;
    }
  }, [idToken, realAccessToken, objectUrl]);

  /**
   * Compose the final ``src`` string for the media element: presigned S3 URL
   * + optional ``#t=<stillFrame>`` for still-frame thumbnails.
   *
   * Trim start/end are intentionally NOT encoded as a media fragment. Toggling
   * edit mode used to strip ``#t=start,end``, which changed ``src`` and reloaded
   * the element to a black frame. Start seek + end pause are handled in JS
   * (``applyConfiguredStartTime`` / timeupdate) so the URL stays stable.
   */
  const usableMediaSrc = useMemo<string | null>(() => {
    if (!presignedUrl || !mediaItem) return null;
    const safePresignedUrl = sanitizeHttpUrlForBrowserLoad(presignedUrl);
    if (
      useStillFrame &&
      mediaItem.mediaContext?.stillFrame &&
      !isDataUrlImage(mediaItem.mediaContext.stillFrame) &&
      !isLegacyStillFrameTimeIndex(mediaItem.mediaContext.stillFrame) &&
      mediaItem.mediaType === 'video'
    ) {
      return `${safePresignedUrl}#t=${mediaItem.mediaContext.stillFrame}`;
    }
    return safePresignedUrl;
  }, [
    presignedUrl,
    useStillFrame,
    mediaItem?.mediaType,
    mediaItem?.mediaContext?.stillFrame,
  ]);

  // Re-apply configured start on each new media src (fullscreen remount, trim change while not editing).
  useEffect(() => {
    hasAppliedStartTimeRef.current = false;
  }, [usableMediaSrc]);

  // Responsive beside → below collapse: measure the outer player+transcript row
  // (containerRef, set below) and flag when it is too narrow to show both side by
  // side. This is independent of isFullscreen/isPresentation/isEventModal — the
  // same check applies everywhere the row can render (Nic follow-up, TR-1589).
  //
  // Depends on usableMediaSrc: while media is still presigning, the component
  // returns the "Loading media" placeholder further down and containerRef never
  // mounts. An effect with `[]` deps would see a null ref on that first pass and
  // never re-check once the real (ref-bearing) player mounted a moment later —
  // exactly the case for any video that takes a beat to load. Re-running this
  // once usableMediaSrc flips from empty to a real URL fixes that.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const applyWidth = (width: number) => {
      setIsNarrowForSideTranscript(
        width > 0 && width + narrowLayoutWidthAllowancePx < SIDE_TRANSCRIPT_MIN_CONTAINER_WIDTH_PX
      );
    };
    applyWidth(el.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? el.getBoundingClientRect().width;
      applyWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [usableMediaSrc, narrowLayoutWidthAllowancePx]);

  // Side transcript height must match the player's *actual* rendered height (video +
  // Track + Controls), read via getPlayerHeight() below. Reading playerRef.current.
  // clientHeight synchronously during render only reflects the *last committed* DOM
  // state — handleLoadedMetadata's post-metadata resize (setVideoLayoutStyles) commits
  // in the very render pass that would otherwise recompute this transcript height, so
  // that read races one commit behind and can stick at a pre-fit/fallback height. The
  // *other* ResizeObserver above (on containerRef's presentation ancestor) can't cover
  // for this either — in presentation that ancestor's height is fixed by the slide
  // layout and never changes just because the *video inside it* resizes, so it never
  // fires to correct this. Observing playerRef itself (state, not a plain read)
  // guarantees a fresh re-render exactly when the player's real height settles
  // (Bugbot follow-up, TR-1589).
  const [measuredPlayerHeight, setMeasuredPlayerHeight] = useState(0);
  useLayoutEffect(() => {
    const el = playerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const applyHeight = (height: number) => {
      setMeasuredPlayerHeight((prev) => (
        height > 0 && Math.abs(prev - height) >= LAYOUT_HEIGHT_HYSTERESIS_PX ? height : prev
      ));
    };
    applyHeight(el.getBoundingClientRect().height);
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? el.getBoundingClientRect().height;
      applyHeight(height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [usableMediaSrc]);

  useEffect(() => {
    if (mediaItem?.mediaContext?.showTranscript !== undefined) {
      setShowTranscriptState(mediaItem.mediaContext.showTranscript);
    }
    if (mediaItem?.mediaContext?.showTranscriptMarkers !== undefined && searchQuery.trim() === '') {
      setShowTranscriptMarkersState(mediaItem.mediaContext.showTranscriptMarkers);
    }
    // Prefer persisted mediaContext, then the resolved prop from MediaDisplay.
    // Do not force 'side' in fullscreen — that ignored a saved 'below' on enter.
    if (
      mediaItem?.mediaContext?.transcriptPosition === 'side' ||
      mediaItem?.mediaContext?.transcriptPosition === 'below'
    ) {
      setTranscriptPosition(mediaItem.mediaContext.transcriptPosition);
    } else if (
      providedTranscriptPosition === 'side' ||
      providedTranscriptPosition === 'below'
    ) {
      setTranscriptPosition(providedTranscriptPosition);
    }
    if (mediaItem?.mediaContext?.autoScroll !== undefined) {
      setAutoScroll(mediaItem.mediaContext.autoScroll);
    } else {
      // Default locally only — persisting on mount marks the event modal dirty.
      setAutoScroll(true);
    }
    if (mediaItem?.mediaContext?.rotation !== undefined) {
      setTotalRotation(mediaItem?.mediaContext?.rotation || 0);
    }
  }, [
    mediaItem?.mediaContext?.transcriptPosition,
    mediaItem?.mediaContext?.showTranscript,
    mediaItem?.mediaContext?.showTranscriptMarkers,
    mediaItem?.mediaContext?.autoScroll,
    mediaItem?.mediaContext?.rotation,
    mediaItem?.mediaType,
    providedTranscriptPosition,
  ]);

  // Reset end-time override when the trim window or player presentation context
  // changes. Kept separate from the transcript/rotation sync effect so unrelated
  // mediaContext updates (autoScroll, markers, etc.) do not clear the override,
  // but fullscreen / mediaType swaps still re-enable pause-at-end.
  useEffect(() => {
    setHasManuallyOverriddenEndTime(false);
  }, [
    mediaItem?.mediaContext?.startTime,
    mediaItem?.mediaContext?.endTime,
    mediaItem?.mediaType,
    isFullscreen,
  ]);

  useEffect(() => {
    if (searchQuery.trim() !== '') {
      if (!showTranscriptMarkersState) {
        setShowTranscriptMarkersState(true);
        if (updateMediaContext) {
          updateMediaContext('showTranscriptMarkers', true);
        }
      }
    } else {
      if (showTranscriptMarkersState !== showTranscriptMarkers) {
        setShowTranscriptMarkersState(showTranscriptMarkers);
        if (updateMediaContext) {
          updateMediaContext('showTranscriptMarkers', showTranscriptMarkers);
        }
      }
    }
  }, [searchQuery]);

  const processTranscript = (data: any): TranscriptItem[] => {
    const transcriptItems = data.map((transcript: any) => {
      const startInSeconds = getSecondsFromTimeString(transcript.start);
      const endInSeconds = getSecondsFromTimeString(transcript.end);
      const text = transcript.segment ? transcript.segment.trim() : transcript.text ? transcript.text.trim() : '';
      return {
        speaker: transcript.speaker || undefined,
        start: startInSeconds,
        end: endInSeconds,
        text: text,
      }
    });
    return transcriptItems;
  }

  useEffect(() => {
    const loadTranscript = async () => {
      if (!transcriptSrc || transcriptSrc.trim() === '' || !isTranscriptJsonLocation(transcriptSrc) || !orgId || !accessToken) return;
      try {
        let response = null;
        let dataText = undefined;
        let apiUrl = `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(transcriptSrc)}`;
        response = await fetch(apiUrl, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          method: 'GET',
        });
        const responseForTextTest = response.clone();
        try {
          const data = await response.json();
          if ((data as any).transcript) {
            const processedTranscript = processTranscript((data as any).transcript);
            setTranscript(processedTranscript);
            setTranscriptText(undefined);
          } else {
            const responseText = await responseForTextTest.text();
            dataText = responseText;
            setTranscriptText(dataText);
          }
        } catch (error) {
          const responseText = await responseForTextTest.text();
          dataText = responseText;
          setTranscriptText(dataText);
        }
      } catch (error) {
        console.error('Error loading transcript:', error);
        setTranscript([]);
        setTranscriptText(undefined);
      }
    };
    if (!useStillFrame && orgId) {
      loadTranscript();
    }
  }, [transcriptSrc, useStillFrame, orgId, accessToken]);

  useEffect(() => {
    if (transcript.length > 0) {
      setActiveSegmentIndex(0); // Initialize to the first segment
    }
  }, [transcript]);

  /**
   * Seek to ``mediaContext.startTime`` once per src load. Trim windows are not
   * encoded in the media URL (see ``usableMediaSrc``), so this is the primary
   * path for opening at the configured start for both audio and video.
   */
  const applyConfiguredStartTime = () => {
    if (hasAppliedStartTimeRef.current || !mediaRef.current || isEditingMediaContext) {
      return;
    }
    const rawStart =
      mediaItem?.mediaContext?.startTime ?? mediaItem?.sourceContext?.startTime;
    if (rawStart == null || rawStart === '' || rawStart === 0 || rawStart === '0') {
      hasAppliedStartTimeRef.current = true;
      return;
    }
    const startTimeSeconds = getSecondsFromTimeString(rawStart);
    if (Number.isNaN(startTimeSeconds) || startTimeSeconds <= 0) {
      hasAppliedStartTimeRef.current = true;
      return;
    }
    mediaRef.current.currentTime = startTimeSeconds;
    setCurrentTime(startTimeSeconds);
    hasAppliedStartTimeRef.current = true;
  };

  /** Seek the media element and sync React time state (trim-handle frame preview). */
  const seekToTime = (time: number) => {
    const clamped = Math.max(0, Math.min(duration || time, time));
    if (mediaRef.current) {
      // While trimming, keep the element paused so the frame at the handle stays on screen
      if (isEditingMediaContext && !mediaRef.current.paused) {
        mediaRef.current.pause();
        setIsPlaying(false);
      }
      mediaRef.current.currentTime = clamped;
    }
    setCurrentTime(clamped);
  };

  useEffect(() => {
    if (!captureMode || !mediaRef.current) return;
    if (!mediaRef.current.paused) {
      mediaRef.current.pause();
      setIsPlaying(false);
    }
  }, [captureMode]);

  /** After cancel / restore, seek to the reverted start (context update is async). */
  const restoreTrimTimesPreview = (rawStart: unknown) => {
    if (rawStart == null || rawStart === '' || rawStart === 0 || rawStart === '0') {
      seekToTime(0);
      hasAppliedStartTimeRef.current = true;
      return;
    }
    const startSec = getSecondsFromTimeString(
      typeof rawStart === 'number' || typeof rawStart === 'string' ? rawStart : String(rawStart),
    );
    if (!Number.isNaN(startSec) && startSec >= 0) {
      seekToTime(startSec);
      hasAppliedStartTimeRef.current = true;
    }
  };

  // When entering edit mode, pause and show the start-handle frame so the canvas isn't black.
  useEffect(() => {
    if (!isEditingMediaContext || !mediaRef.current) return;
    if (isPlaying) {
      mediaRef.current.pause();
      setIsPlaying(false);
    }
    const rawStart = mediaItem?.mediaContext?.startTime;
    if (rawStart == null || rawStart === '' || rawStart === 0 || rawStart === '0') {
      seekToTime(0);
      return;
    }
    const startTimeSeconds = getSecondsFromTimeString(rawStart);
    if (!Number.isNaN(startTimeSeconds) && startTimeSeconds >= 0) {
      seekToTime(startTimeSeconds);
    }
    // Only on edit-mode enter; handle drags seek themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingMediaContext]);

  // Same-file chip switch / saved trim: re-seek when parent trim changes (player is not remounted).
  useEffect(() => {
    if (isEditingMediaContext || !mediaRef.current) return;
    hasAppliedStartTimeRef.current = false;
    const rawStart =
      mediaItem?.mediaContext?.startTime ?? mediaItem?.sourceContext?.startTime;
    if (rawStart == null || rawStart === '' || rawStart === 0 || rawStart === '0') {
      seekToTime(0);
      hasAppliedStartTimeRef.current = true;
      return;
    }
    applyConfiguredStartTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaItem?.mediaContext?.startTime, mediaItem?.mediaContext?.endTime, isEditingMediaContext]);

  /** Size the video frame to a fixed cap (below-stack split / locked split refit). */
  const applyBelowStackVideoCap = (
    videoCapPx: number,
    videoRatio: number,
    baseWidth: number,
    rotationDeg: number,
    swapWidthAndHeight: boolean,
    zoomScale: number,
    noZoom: boolean,
  ) => {
    if (!(videoRatio > 0) || videoCapPx <= 0) return;
    let finalH = videoCapPx;
    let finalW = Math.round(finalH * videoRatio);
    if (!noZoom && belowStackZoomBaselineRef.current) {
      const zoomed = applyInlineBelowStackZoom(
        belowStackZoomBaselineRef.current,
        zoomScale,
        videoRatio,
        pinnedBelowStackOuterHeightRef.current,
      );
      finalH = zoomed.videoFramePx;
      finalW = zoomed.videoWidthPx;
      pinnedBelowStackOuterHeightRef.current = zoomed.stackOuterPx;
      setBelowStackPinnedOuterHeightPx(zoomed.stackOuterPx);
      if (
        belowStackSplitLockedRef.current
        && belowStackTranscriptPx !== zoomed.transcriptPx
      ) {
        setBelowStackTranscriptPx(zoomed.transcriptPx);
      }
    } else if (!noZoom) {
      const scaled = scaleInlineVideoFrame(videoCapPx, zoomScale, videoRatio, baseWidth);
      finalH = scaled.videoFramePx;
      finalW = scaled.videoWidthPx;
    } else if (baseWidth > 0 && finalW > baseWidth) {
      finalW = baseWidth;
      finalH = Math.round(finalW / videoRatio);
    }
    const widthToUse = swapWidthAndHeight ? finalH : finalW;
    const heightToUse = swapWidthAndHeight ? finalW : finalH;
    setVideoLayoutStyles(
      {
        width: `${finalW}px`,
        height: `${finalH}px`,
        maxWidth: noZoom ? '100%' : 'none',
        flexShrink: 0,
        overflow: 'hidden',
      },
      {
        width: `${widthToUse}px`,
        height: `${heightToUse}px`,
        objectFit: 'contain',
        rotate: `${rotationDeg}deg`,
      },
    );
  };

  const setVideoLayoutStyles = (
    nextCenterer: React.CSSProperties,
    nextVideo: React.CSSProperties,
  ) => {
    setVideoCentererStyle((prev) => {
      if (
        prev.width === nextCenterer.width &&
        prev.height === nextCenterer.height &&
        prev.maxWidth === nextCenterer.maxWidth &&
        prev.flexShrink === nextCenterer.flexShrink &&
        prev.overflow === nextCenterer.overflow
      ) {
        return prev;
      }
      return nextCenterer;
    });
    setVideoStyle((prev) => {
      if (
        prev.width === nextVideo.width &&
        prev.height === nextVideo.height &&
        prev.objectFit === nextVideo.objectFit &&
        prev.rotate === nextVideo.rotate
      ) {
        return prev;
      }
      return nextVideo;
    });
  };

  const handleLoadedMetadata = () => {
    applyConfiguredStartTime();
    // Trim-handle drags only change start/end — keep the fitted frame size locked
    // so seeking/preview does not re-measure and jitter the player.
    if (trimDragActiveRef.current) return;
    // Between-panel split drag applies layout directly on the DOM for smoothness.
    if (belowStackSplitDragActiveRef.current) return;
    if (sideStackSplitDragActiveRef.current) return;
    if (belowGripDragActiveRef.current) return;
    if (!!mediaRef.current && mediaItem.mediaType === 'video') {
      const video = mediaRef.current as HTMLVideoElement;
      if (!rectRef.current) {
        rectRef.current = video.getBoundingClientRect();
      }

      const rotationDeg = Number(totalRotation) || 0;
      const swapWidthAndHeight = rotationDeg === 90 || rotationDeg === -90 || rotationDeg === 270;
      // Displayed aspect ratio (width / height) after accounting for 90°/270° rotation.
      const videoRatio = swapWidthAndHeight
        ? video.videoHeight / video.videoWidth
        : video.videoWidth / video.videoHeight;

      setIsPortrait(videoRatio > 0 ? videoRatio < 1 : false);

      // SourceCard / presentation: fill column width. Height follows AR. Zoom scales
      // from that baseline (same step scale as fullscreen). Transcript stacks below.
      if (isPresentation && !isFullscreen) {
        if (!(videoRatio > 0)) {
          return;
        }
        const zoomScale = deferredFullscreenZoom.previewScale;
        const noZoom = !(zoomScale > 0) || Math.abs(zoomScale - 1) < 0.001;
        const hasTranscriptLoaded =
          showTranscriptState && !!transcriptSrc && transcript && transcript.length > 0;
        // SourceCard / Evidence Locker scrollport: reserve below-transcript space as
        // soon as we know a transcript URL exists — do not wait for the JSON fetch.
        // Video metadata often fires before transcript.length > 0; sizing to full
        // natural height on loadedmetadata then pushes the transcript below the card.
        const scrollportForSoftFitEarly = containerRef.current?.closest(
          '[data-source-card-scrollport]',
        ) as HTMLElement | null;
        const pendingBelowTranscriptInScrollport =
          scrollportForSoftFitEarly != null
          && showTranscriptState
          && layoutTranscriptPosition === 'below'
          && (
            !!transcriptSrc
            || mediaItem?.mediaContext?.transcriptPosition === 'below'
          );
        const needsBelowReserve =
          layoutTranscriptPosition === 'below'
          && (hasTranscriptLoaded || pendingBelowTranscriptInScrollport);
        // Side is the default layoutTranscriptPosition even when there is no transcript
        // to show (hidden, not yet transcribed, etc.) — only reserve the side column's
        // width when a transcript panel will actually render there. Otherwise every
        // transcript-less presentation video was permanently narrowed by
        // FULLSCREEN_SIDE_TRANSCRIPT_WIDTH_RESERVE_PX for a column that never renders
        // (Bugbot follow-up, TR-1589).
        const needsSideWidthReserve = hasTranscriptLoaded && layoutTranscriptPosition === 'side';
        // A real height budget only exists when the caller passed an explicit
        // fitHeight — genuine presentation slides always resolve one (mediaDisplay.tsx's
        // documentFitHeightRaw falls back to normalContainerHeight whenever isPresentation
        // is set), and fillContainer/event-modal SourceCard previews pass an explicit
        // containerHeight too. A plain, page-scrolling SourceCard citation never gets a
        // fitHeight — growing to the video's natural size and letting the card/page
        // scroll is its whole contract (pre-TR-1589 behavior). Gating strictly on
        // fitHeight (not a containerDimensions/getBoundingClientRect fallback, which can
        // read an incidental/stale measurement) keeps that citation contract intact.
        const hasHeightBudget = fitHeight != null && fitHeight > 0;
        // Evidence Locker / SourceCard video: no locked fitHeight. Measure a soft
        // scrollport budget from stable CSS max-heights (not content height) so we
        // only cap when the natural video+transcript stack would overflow.
        let softBelowBudget = 0;
        const scrollportForSoftFit = scrollportForSoftFitEarly;
        const isElScrollport = isEvidenceLockerScrollport(scrollportForSoftFit);
        const useSoftBelowFit =
          !hasHeightBudget && needsBelowReserve && scrollportForSoftFit != null;
        if (useSoftBelowFit) {
          const measured = measureSoftBelowTranscriptBudget(containerRef.current);
          const widthKey =
            (fitWidth && fitWidth > 0 ? Math.round(fitWidth) : 0) ||
            containerDimensions.width ||
            Math.round(containerRef.current?.getBoundingClientRect().width || 0);
          if (
            pinnedSoftBelowBudgetWidthRef.current > 0 &&
            Math.abs(pinnedSoftBelowBudgetWidthRef.current - widthKey) >= 2
          ) {
            pinnedSoftBelowBudgetRef.current = null;
            softBelowOverflowCeilRef.current = null;
            userGripBelowTranscriptPxRef.current = null;
            userGripSoftBelowBudgetPxRef.current = null;
            userGripBelowChromePxRef.current = null;
          }
          if (measured > 0) {
            if (isElScrollport) {
              // Evidence Locker: popover height settles after first paint — keep
              // updating, but never below a stack the user committed with the grip.
              // The overflow ceil below still caps this.
              const elBudget = Math.max(
                measured,
                userGripSoftBelowBudgetPxRef.current ?? 0,
              );
              pinnedSoftBelowBudgetRef.current = elBudget;
              pinnedSoftBelowBudgetWidthRef.current = widthKey;
              softBelowBudget = elBudget;
            } else if (pinnedSoftBelowBudgetRef.current == null) {
              // Answer-item: pin once; later content-sized measures must not shrink the ceiling.
              pinnedSoftBelowBudgetRef.current = measured;
              pinnedSoftBelowBudgetWidthRef.current = widthKey;
              softBelowBudget = measured;
            } else {
              softBelowBudget = pinnedSoftBelowBudgetRef.current;
            }
          } else if (pinnedSoftBelowBudgetRef.current != null) {
            softBelowBudget = pinnedSoftBelowBudgetRef.current;
          }
          if (softBelowBudget <= 0 && containerRef.current) {
            const fallback = measureScrollportSoftBudgetFallback(containerRef.current);
            if (fallback > 0) {
              if (pinnedSoftBelowBudgetRef.current == null) {
                pinnedSoftBelowBudgetRef.current = fallback;
                pinnedSoftBelowBudgetWidthRef.current = widthKey;
              }
              softBelowBudget = pinnedSoftBelowBudgetRef.current ?? fallback;
            }
          }
          if (softBelowBudget > 0) {
            softBelowMetadataRefitCountRef.current = 0;
          }
          if (
            isElScrollport &&
            softBelowOverflowCeilRef.current != null &&
            softBelowBudget > softBelowOverflowCeilRef.current
          ) {
            softBelowBudget = softBelowOverflowCeilRef.current;
          }
        } else {
          pinnedSoftBelowBudgetRef.current = null;
          pinnedSoftBelowBudgetWidthRef.current = 0;
          softBelowOverflowCeilRef.current = null;
          userGripBelowTranscriptPxRef.current = null;
          userGripSoftBelowBudgetPxRef.current = null;
          userGripBelowChromePxRef.current = null;
          if (softBelowTranscriptPanelHeight != null) {
            setSoftBelowTranscriptPanelHeight(null);
          }
        }
        const effectiveHasHeightBudget =
          hasHeightBudget
          || softBelowBudget > 0
          || (
            belowStackSplitLockedRef.current
            && pinnedBelowStackOuterHeightRef.current != null
          );
        // loadedmetadata often beats scrollport layout; defer rather than expand to
        // full natural height and push a below transcript out of the scrollport.
        if (
          needsBelowReserve
          && scrollportForSoftFit
          && !hasHeightBudget
          && softBelowBudget <= 0
          && softBelowMetadataRefitCountRef.current < 4
        ) {
          softBelowMetadataRefitCountRef.current += 1;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              handleLoadedMetadata();
            });
          });
          return;
        }
        // Plain citation without a below transcript: natural width/height.
        if (
          noZoom &&
          !swapWidthAndHeight &&
          !effectiveHasHeightBudget &&
          !needsSideWidthReserve &&
          !needsBelowReserve
        ) {
          if (softBelowTranscriptPanelHeight != null) {
            setSoftBelowTranscriptPanelHeight(null);
          }
          setVideoLayoutStyles(
            {
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              flexShrink: 0,
              overflow: 'hidden',
            },
            {
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              rotate: `${rotationDeg}deg`,
            },
          );
          return;
        }
        // CSS `rotate` spins the box in place without reflowing it, so a 90°/270°
        // rotated video needs its element width/height swapped relative to the
        // displayed AR — otherwise the rotated frame renders inside a box shaped
        // for the *unrotated* orientation (pillarboxed/cropped). `height: 'auto'`
        // above relies on the browser deriving height from the native intrinsic
        // ratio, which is only correct when there is nothing to swap.
        //
        // Height budget: the slide slot (tp-presentation's mediaHeight) is a fixed-
        // height, overflow-hidden box holding video + Track + Controls (+ a below
        // transcript). Sizing the video purely from its own aspect ratio at 100%
        // width ignores that budget entirely and can push Track/Controls — or an
        // entire below transcript — out of the clipped area (TR-1589 follow-up:
        // hiding the transcript reverted the video to full height and took the
        // settings/gear control with it). Reserve room for chrome only when there is
        // an actual budget to respect (hasHeightBudget); reserve more when a below
        // transcript is also going to render.
        let baseWidth =
          (fitWidth && fitWidth > 0 ? Math.round(fitWidth) : 0)
          || containerDimensions.width
          || Math.round(videoCentererRef.current?.parentElement?.getBoundingClientRect().width || 0)
          || Math.round(containerRef.current?.getBoundingClientRect().width || 0);
        // When a side transcript is actually rendering it claims its own flex column
        // in the row. baseWidth above reflects the full container (player + transcript),
        // so subtract the same reserve used by the non-presentation side path to avoid
        // sizing the video wrapper wider than its actual flex-1 share of the row.
        // Gated on needsSideWidthReserve (not just layoutTranscriptPosition === 'side')
        // so a hidden/missing transcript doesn't narrow the video for a column that
        // never renders.
        if (needsSideWidthReserve) {
          if (
            sideStackSplitLockedRef.current
            && sideStackTranscriptPx != null
            && pinnedSideStackOuterWidthRef.current != null
          ) {
            baseWidth = Math.max(
              SIDE_PLAYER_MIN_WIDTH_PX,
              pinnedSideStackOuterWidthRef.current
                - SIDE_STACK_SPLITTER_TOTAL_PX
                - sideStackTranscriptPx,
            );
          } else {
            baseWidth = Math.max(120, baseWidth - FULLSCREEN_SIDE_TRANSCRIPT_WIDTH_RESERVE_PX);
          }
        }
        if (baseWidth <= 0) {
          setVideoLayoutStyles(
            {
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              flexShrink: 0,
              overflow: 'hidden',
            },
            {
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              rotate: `${rotationDeg}deg`,
            },
          );
          return;
        }

        // Displayed (post-rotation) box, sized to fill the column at 100% zoom.
        const zoom1VideoH = Math.round(baseWidth / videoRatio);
        let finalW: number;
        let finalH: number;
        if (noZoom) {
          finalW = baseWidth;
          finalH = zoom1VideoH;
        } else {
          const baselineVideoH =
            belowStackZoomBaselineRef.current?.videoFramePx ?? zoom1VideoH;
          const scaled = scaleInlineVideoFrame(
            baselineVideoH,
            zoomScale,
            videoRatio,
            baseWidth,
          );
          finalW = scaled.videoWidthPx;
          finalH = scaled.videoFramePx;
        }
        const displayW = finalW;
        const displayH = finalH;
        // Soft-fit may run in this pass before a zoom baseline exists; keep its capped
        // split so the zoom-seed path below does not rebuild from natural zoom-1 sizes.
        let samePassSoftFitSplit: {
          videoFramePx: number;
          transcriptPx: number;
        } | null = null;
        if (effectiveHasHeightBudget) {
          if (
            needsBelowReserve
            && belowStackSplitLockedRef.current
            && belowStackTranscriptPx != null
            && pinnedBelowStackOuterHeightRef.current != null
          ) {
            const chromePx =
              belowStackChromePxRef.current > 0
                ? belowStackChromePxRef.current
                : measureSoftBelowChromePx(
                    playerRef.current,
                    videoCentererRef.current,
                  );
            const videoCap = Math.max(
              SOFT_BELOW_VIDEO_MIN_PX,
              pinnedBelowStackOuterHeightRef.current
                - BELOW_STACK_SPLITTER_TOTAL_PX
                - chromePx
                - belowStackTranscriptPx,
            );
            applyBelowStackVideoCap(
              videoCap,
              videoRatio,
              baseWidth,
              rotationDeg,
              swapWidthAndHeight,
              zoomScale,
              noZoom,
            );
            return;
          }
          const pinnedStackOuter = belowStackSplitLockedRef.current
            ? pinnedBelowStackOuterHeightRef.current
            : null;
          const availableHeight =
            pinnedStackOuter != null && pinnedStackOuter > 0
              ? pinnedStackOuter
              : hasHeightBudget
                ? Math.round(fitHeight as number)
                : softBelowBudget;
          let capHeight: number;
          // Zoom owns the below-stack split once a baseline exists. Soft-fit's live
          // chrome / scrollport remasure would otherwise fight applyInlineBelowStackZoom
          // and jitter the transcript bottom by 1–2px after zoom-out.
          const zoomOwnsBelowStack =
            !isZoomScaleOne(zoomScale) && belowStackZoomBaselineRef.current != null;
          if (zoomOwnsBelowStack) {
            capHeight = displayH;
          } else if (!hasHeightBudget && needsBelowReserve) {
            const chromePx = measureSoftBelowChromePx(
              playerRef.current,
              videoCentererRef.current,
            );
            const gripTranscriptPx = belowStackSplitLockedRef.current
              ? null
              : userGripBelowTranscriptPxRef.current;
            const transcriptOverride = belowStackSplitLockedRef.current
              ? belowStackTranscriptPx
              : gripTranscriptPx;
            // A grip-committed budget is a measured stack total that already contains
            // the real chrome, so keeping the reserve that `measureSoftBelowChromePx`
            // substitutes for a sub-40px measurement subtracts chrome twice and
            // collapses the video cap. Prefer the value captured at release (settled
            // layout) over a fresh measure, and never raise the subtraction.
            const gripChromePx =
              userGripBelowChromePxRef.current
              ?? measureExactBelowChromePx(playerRef.current, videoCentererRef.current);
            const softFitChromePx =
              gripTranscriptPx != null && gripChromePx != null
                ? Math.min(chromePx, gripChromePx)
                : chromePx;
            if (scrollportForSoftFit) {
              // SourceCard / Evidence Locker scrollport: always split the budget.
              // Letting the natural-fit branch restore full displayH on loadedmetadata
              // pushes the below transcript out of the first viewport.
              const fitted = fitSoftBelowStack(
                availableHeight,
                softFitChromePx,
                transcriptOverride,
                gripTranscriptPx != null,
              );
              capHeight = fitted.videoCap;
              samePassSoftFitSplit = {
                videoFramePx: fitted.videoCap,
                transcriptPx: fitted.transcriptH,
              };
              if (!belowStackSplitLockedRef.current) {
                commitSoftBelowTranscriptPanelHeight(fitted.transcriptH);
              }
            } else {
              const naturalStackChromePx = Math.max(
                chromePx,
                PRESENTATION_PLAYER_CHROME_RESERVE_PX,
              );
              const naturalStack =
                displayH +
                naturalStackChromePx +
                BELOW_STACK_SPLITTER_TOTAL_PX +
                PRESENTATION_BELOW_TRANSCRIPT_RESERVE_PX +
                SOFT_BELOW_STACK_SLACK_PX;
              const alreadySoftFit = softBelowTranscriptPanelHeight != null;
              if (naturalStack <= availableHeight && !alreadySoftFit) {
                capHeight = displayH;
                if (softBelowTranscriptPanelHeight != null) {
                  commitSoftBelowTranscriptPanelHeight(null);
                }
              } else {
                const fitted = fitSoftBelowStack(
                  availableHeight,
                  softFitChromePx,
                  transcriptOverride,
                  gripTranscriptPx != null,
                );
                capHeight = fitted.videoCap;
                samePassSoftFitSplit = {
                  videoFramePx: fitted.videoCap,
                  transcriptPx: fitted.transcriptH,
                };
                if (!belowStackSplitLockedRef.current) {
                  commitSoftBelowTranscriptPanelHeight(fitted.transcriptH);
                }
              }
            }
          } else {
            // Locked slide budgets protect a taller video floor and may shrink the
            // transcript reserve.
            const MIN_PRESENTATION_VIDEO_HEIGHT_PX = 220;
            const desiredBelowReserve = needsBelowReserve
              ? PRESENTATION_BELOW_TRANSCRIPT_RESERVE_PX + BELOW_STACK_SPLITTER_TOTAL_PX
              : 0;
            const maxAffordableBelowReserve = Math.max(
              0,
              availableHeight - PRESENTATION_PLAYER_CHROME_RESERVE_PX - MIN_PRESENTATION_VIDEO_HEIGHT_PX
            );
            const effectiveBelowReserve = Math.min(desiredBelowReserve, maxAffordableBelowReserve);
            const reserve = PRESENTATION_PLAYER_CHROME_RESERVE_PX + effectiveBelowReserve;
            capHeight = Math.max(MIN_PRESENTATION_VIDEO_HEIGHT_PX, availableHeight - reserve);
            if (softBelowTranscriptPanelHeight != null) {
              commitSoftBelowTranscriptPanelHeight(null);
            }
          }
          if (!hasHeightBudget && needsBelowReserve) {
            // Soft-fit: shrink overflow only at zoom 1 — zoom-in grows the player stack.
            if (isZoomScaleOne(zoomScale) && displayH > capHeight) {
              finalH = capHeight;
              finalW = Math.round(capHeight * videoRatio);
            }
          } else if (displayH > capHeight) {
            finalH = capHeight;
            finalW = Math.round(capHeight * videoRatio);
          }
        }
        if (
          needsBelowReserve
          && scrollportForSoftFit
          && !effectiveHasHeightBudget
          && isZoomScaleOne(zoomScale)
        ) {
          return;
        }
        if (needsBelowReserve) {
          const chromePxForZoom = measureSoftBelowChromePx(
            playerRef.current,
            videoCentererRef.current,
          );
          const transcriptPxNow =
            belowStackSplitLockedRef.current && belowStackTranscriptPx != null
              ? belowStackTranscriptPx
              : softBelowTranscriptPanelHeight ?? TRANSCRIPT_MIN_HEIGHT_PX;

          // Width jitter (scrollbar) must not drop the zoom baseline while zoomed —
          // without it soft-fit remasures every frame and the transcript bottom oscillates.
          if (
            isZoomScaleOne(zoomScale)
            && Math.abs(baseWidth - belowStackZoomBaselineWidthRef.current) >= 2
          ) {
            belowStackZoomBaselineRef.current = null;
          }

          if (isZoomScaleOne(zoomScale) && !belowStackSplitDragActiveRef.current) {
            belowStackZoomBaselineRef.current = {
              videoFramePx: finalH,
              transcriptPx: transcriptPxNow,
              stackOuterPx:
                finalH
                + chromePxForZoom
                + BELOW_STACK_SPLITTER_TOTAL_PX
                + transcriptPxNow,
              chromePx: chromePxForZoom,
              baseWidth,
            };
            belowStackZoomBaselineWidthRef.current = baseWidth;
            const pinnedStack = pinnedBelowStackOuterHeightRef.current;
            if (
              pinnedStack != null
              && pinnedStack > belowStackZoomBaselineRef.current.stackOuterPx
            ) {
              const restored = applyInlineBelowStackZoom(
                belowStackZoomBaselineRef.current,
                zoomScale,
                videoRatio,
                pinnedStack,
              );
              finalH = restored.videoFramePx;
              finalW = restored.videoWidthPx;
              if (!belowStackSplitLockedRef.current) {
                commitSoftBelowTranscriptPanelHeight(restored.transcriptPx);
              }
              setBelowStackPinnedOuterHeightPx(restored.stackOuterPx);
            } else if (
              belowStackPinnedOuterHeightPx != null
              && !belowStackSplitLockedRef.current
            ) {
              pinnedBelowStackOuterHeightRef.current = null;
              setBelowStackPinnedOuterHeightPx(null);
            }
          } else if (!isZoomScaleOne(zoomScale)) {
            // Seed a zoom-1 baseline if soft-fit never captured one (or width jitter
            // cleared it before zoom applied). Prefer the soft-fit split from this
            // same pass — natural zoom1VideoH + default transcript reserve can be
            // taller than the capped budget, and zoomOwnsBelowStack would then lock
            // that oversized stack until zoom returns to 1.
            if (!belowStackZoomBaselineRef.current) {
              const seedVideoPx = samePassSoftFitSplit != null
                ? Math.min(zoom1VideoH, samePassSoftFitSplit.videoFramePx)
                : zoom1VideoH;
              const seedTranscriptPx =
                samePassSoftFitSplit?.transcriptPx ?? transcriptPxNow;
              belowStackZoomBaselineRef.current = {
                videoFramePx: seedVideoPx,
                transcriptPx: seedTranscriptPx,
                stackOuterPx:
                  seedVideoPx
                  + chromePxForZoom
                  + BELOW_STACK_SPLITTER_TOTAL_PX
                  + seedTranscriptPx,
                chromePx: chromePxForZoom,
                baseWidth,
              };
              belowStackZoomBaselineWidthRef.current = baseWidth;
            }
            const zoomed = applyInlineBelowStackZoom(
              belowStackZoomBaselineRef.current,
              zoomScale,
              videoRatio,
              pinnedBelowStackOuterHeightRef.current,
            );
            finalH = zoomed.videoFramePx;
            finalW = zoomed.videoWidthPx;
            pinnedBelowStackOuterHeightRef.current = zoomed.stackOuterPx;
            setBelowStackPinnedOuterHeightPx(zoomed.stackOuterPx);
            if (belowStackSplitLockedRef.current) {
              if (
                belowStackTranscriptPx == null
                || Math.abs(belowStackTranscriptPx - zoomed.transcriptPx) >= LAYOUT_HEIGHT_HYSTERESIS_PX
              ) {
                setBelowStackTranscriptPx(zoomed.transcriptPx);
              }
            } else {
              commitSoftBelowTranscriptPanelHeight(zoomed.transcriptPx);
            }
          }
        } else if (!noZoom) {
          const chromePxForSide = measureSoftBelowChromePx(
            playerRef.current,
            videoCentererRef.current,
          );
          const playerOuterPx = finalH + chromePxForSide;
          if (isZoomScaleOne(zoomScale) && !belowStackSplitDragActiveRef.current) {
            belowStackZoomBaselineRef.current = {
              videoFramePx: finalH,
              transcriptPx: 0,
              stackOuterPx: playerOuterPx,
              chromePx: chromePxForSide,
              baseWidth,
            };
            belowStackZoomBaselineWidthRef.current = baseWidth;
          } else {
            const nextOuter = Math.max(
              pinnedPresentationPlayerOuterRef.current ?? playerOuterPx,
              playerOuterPx,
            );
            pinnedPresentationPlayerOuterRef.current = nextOuter;
            setPinnedPresentationPlayerOuterPx(nextOuter);
          }
        }
        const widthToUse = swapWidthAndHeight ? finalH : finalW;
        const heightToUse = swapWidthAndHeight ? finalW : finalH;
        setVideoLayoutStyles(
          {
            width: `${finalW}px`,
            height: `${finalH}px`,
            maxWidth:
              noZoom || finalW >= baseWidth
                ? '100%'
                : 'none',
            flexShrink: 0,
            overflow: 'hidden',
          },
          {
            width: `${widthToUse}px`,
            height: `${heightToUse}px`,
            objectFit: 'contain',
            rotate: `${rotationDeg}deg`,
          },
        );
        return;
      }

      let maxWidth = containerDimensions.width;
      let maxHeight = containerDimensions.height;

      if (isFullscreen && containerRef.current) {
        const playerBudget = measureFullscreenPlayerBudgetPx(containerRef.current);
        if (playerBudget > 0) {
          maxHeight = playerBudget;
        }
      }

      if ((!maxWidth || maxWidth <= 0 || !maxHeight || maxHeight <= 0) && containerRef.current) {
        const ancestor = containerRef.current.closest('.mediaDisplay') as HTMLElement
          || containerRef.current.parentElement as HTMLElement
          || containerRef.current;
        if (ancestor) {
          const rect = ancestor.getBoundingClientRect();
          if (!maxWidth || maxWidth <= 0) {
            maxWidth = Math.round(rect.width);
          }
          if (!maxHeight || maxHeight <= 0) {
            if (isFullscreen) {
              const playerBudget = measureFullscreenPlayerBudgetPx(containerRef.current);
              maxHeight = playerBudget > 0
                ? playerBudget
                : Math.round(
                    rect.height - measureFullscreenToolbarHeightPx(containerRef.current),
                  );
            } else {
              const topOffset = rect.top > 0 ? rect.top : 0;
              maxHeight = Math.round(window.innerHeight - topOffset - 160);
              if (maxHeight <= 0) maxHeight = Math.round(window.innerHeight * 0.6);
            }
          }
        }
      }

      const hasTranscript =
        showTranscriptState && !!transcriptSrc && transcript && transcript.length > 0;
      const eventModalSideLayout =
        isEventModal &&
        !isFullscreen &&
        !isUnifiedSources &&
        hasTranscript &&
        layoutTranscriptPosition === 'side';
      if (hasTranscript) {
        if (layoutTranscriptPosition === 'side') {
          if (eventModalSideLayout) {
            maxWidth = EVENT_MODAL_SIDE_PLAYER_WIDTH_PX;
          } else if (
            sideStackSplitLockedRef.current
            && sideStackTranscriptPx != null
            && pinnedSideStackOuterWidthRef.current != null
          ) {
            maxWidth = Math.max(
              SIDE_PLAYER_MIN_WIDTH_PX,
              pinnedSideStackOuterWidthRef.current
                - SIDE_STACK_SPLITTER_TOTAL_PX
                - sideStackTranscriptPx,
            );
          } else {
            maxWidth = maxWidth - FULLSCREEN_SIDE_TRANSCRIPT_WIDTH_RESERVE_PX;
          }
          // Side transcript: still leave room under the frame for track + controls.
          if (isFullscreen || eventModalSideLayout) {
            maxHeight = Math.max(120, maxHeight - FULLSCREEN_PLAYER_CHROME_HEIGHT_PX);
          }
        } else {
          // Below: shrink video so transcript panel + track/controls stay in view.
          const transcriptReserve = getEffectiveBelowTranscriptHeight();
          if (
            isFullscreen
            && belowStackSplitLockedRef.current
            && belowStackTranscriptPx != null
            && pinnedBelowStackOuterHeightRef.current != null
          ) {
            const chromePx =
              belowStackChromePxRef.current > 0
                ? belowStackChromePxRef.current
                : getFullscreenBelowChromePx(
                    playerRef.current,
                    videoCentererRef.current,
                  );
            const videoCap = Math.max(
              SOFT_BELOW_VIDEO_MIN_PX,
              pinnedBelowStackOuterHeightRef.current
                - BELOW_STACK_SPLITTER_TOTAL_PX
                - chromePx
                - belowStackTranscriptPx,
            );
            applyBelowStackVideoCap(
              videoCap,
              videoRatio,
              maxWidth,
              rotationDeg,
              swapWidthAndHeight,
              deferredFullscreenZoom.previewScale,
              !(deferredFullscreenZoom.previewScale > 0)
                || Math.abs(deferredFullscreenZoom.previewScale - 1) < 0.001,
            );
            return;
          }
          if (isFullscreen) {
            const playerBudget =
              measureFullscreenPlayerBudgetPx(containerRef.current) || maxHeight;
            maxHeight = reserveFullscreenBelowVideoFrameHeight(
              playerBudget,
              transcriptReserve,
              getFullscreenBelowChromePx(playerRef.current, videoCentererRef.current),
            );
          } else {
            maxHeight = reserveBelowTranscriptFromMaxHeight(maxHeight, transcriptReserve);
          }
        }
      } else if (isFullscreen) {
        // Initial fullscreen fit: video frame + track + controls must share the slot
        // (otherwise the frame fills the viewport and chrome sits below the fold).
        maxHeight = Math.max(120, maxHeight - FULLSCREEN_PLAYER_CHROME_HEIGHT_PX);
      }

      if (!maxHeight || maxHeight <= 0 || Number.isNaN(maxHeight)) {
        const naturalHeight = swapWidthAndHeight ? 100 : maxWidth / videoRatio;
        const viewportCap = window.innerHeight * (hasTranscript && layoutTranscriptPosition === 'below' ? 0.45 : 0.7);
        maxHeight = Math.min(naturalHeight, viewportCap);
      }

      // Standalone (non-presentation) viewers keep a moderate height so surrounding
      // page chrome stays reachable. SourceCard / presentation / event-modal fit
      // boxes fill the measured column instead — no artificial height cap.
      const hasExplicitFitBox =
        (fitWidth != null && fitWidth > 0) || (fitHeight != null && fitHeight > 0);
      if (
        !isFullscreen &&
        !isPresentation &&
        !hasExplicitFitBox &&
        !isTable &&
        !isAccordion &&
        !isUnifiedSources
      ) {
        const nonFullscreenVideoCap =
          hasTranscript && layoutTranscriptPosition === 'below' ? 280 : 400;
        maxHeight = Math.min(maxHeight, nonFullscreenVideoCap);
      }

      const naturalWidth = swapWidthAndHeight ? video.videoHeight : video.videoWidth;
      const naturalHeight = swapWidthAndHeight ? video.videoWidth : video.videoHeight;
      // Cap to native resolution only outside presentation/SourceCard so previews
      // can scale up to the container width while keeping aspect ratio.
      if (!isFullscreen && !isPresentation && naturalWidth > 0 && naturalHeight > 0) {
        maxWidth = Math.min(maxWidth, naturalWidth);
        maxHeight = Math.min(maxHeight, naturalHeight);
      }

      if (maxWidth <= 0 || maxHeight <= 0 || !(videoRatio > 0)) {
        return;
      }

      // Contain in the available box: prefer full container width, fall back to
      // height-limited when the aspect ratio would overflow vertically.
      const widthLimitedHeight = maxWidth / videoRatio;
      const heightLimitedWidth = maxHeight * videoRatio;
      const fitToWidth = widthLimitedHeight <= maxHeight + 0.5;
      const displayW = Math.round(fitToWidth ? maxWidth : heightLimitedWidth);
      const displayH = Math.round(fitToWidth ? widthLimitedHeight : maxHeight);

      setIsPortrait(videoRatio < 1);

      // Always set both layout dimensions with correct AR (object-fit as belt/suspenders).
      // For 90°/270°, swap element width/height so CSS rotate maps to the display box.
      let widthToUse = swapWidthAndHeight ? displayH : displayW;
      let heightToUse = swapWidthAndHeight ? displayW : displayH;
      let centererWidth = displayW;
      let centererHeight = displayH;

      const fullscreenBelowWithTranscript =
        isFullscreen
        && hasTranscript
        && layoutTranscriptPosition === 'below'
        && !isAudioFile;

      if (fullscreenBelowWithTranscript && !belowStackSplitLockedRef.current) {
        const zoomScale = deferredFullscreenZoom.previewScale;
        const chromePx = getFullscreenBelowChromePx(
          playerRef.current,
          videoCentererRef.current,
        );
        const playerBudget =
          measureFullscreenPlayerBudgetPx(containerRef.current) || maxHeight;
        const stackOuterPx = playerBudget;
        const transcriptPxNow =
          fullscreenBelowTranscriptHeight != null && fullscreenBelowTranscriptHeight > 0
            ? fullscreenBelowTranscriptHeight
            : getEffectiveBelowTranscriptHeight();

        if (Math.abs(displayW - belowStackZoomBaselineWidthRef.current) >= 2) {
          belowStackZoomBaselineRef.current = null;
        }

        if (isZoomScaleOne(zoomScale) && !belowStackSplitDragActiveRef.current) {
          belowStackZoomBaselineRef.current = {
            videoFramePx: displayH,
            transcriptPx: transcriptPxNow,
            stackOuterPx,
            chromePx,
            baseWidth: displayW,
          };
          belowStackZoomBaselineWidthRef.current = displayW;
          pinnedBelowStackOuterHeightRef.current = null;
          setBelowStackPinnedOuterHeightPx(null);
        } else if (belowStackZoomBaselineRef.current) {
          const baseline = belowStackZoomBaselineRef.current;
          const balanced = rebalanceFullscreenBelowZoom(
            baseline,
            zoomScale,
            videoRatio,
            FULLSCREEN_ZOOM_BELOW_TRANSCRIPT_MIN_PX,
          );
          centererHeight = balanced.videoFramePx;
          centererWidth = Math.round(centererHeight * videoRatio);
          if (baseline.baseWidth > 0 && centererWidth > baseline.baseWidth) {
            centererWidth = baseline.baseWidth;
            centererHeight = Math.round(centererWidth / videoRatio);
          }
          widthToUse = swapWidthAndHeight ? centererHeight : centererWidth;
          heightToUse = swapWidthAndHeight ? centererWidth : centererHeight;
          if (
            fullscreenBelowTranscriptHeight == null
            || Math.abs(fullscreenBelowTranscriptHeight - balanced.transcriptPx)
              >= LAYOUT_HEIGHT_HYSTERESIS_PX
          ) {
            setFullscreenBelowTranscriptHeight(balanced.transcriptPx);
          }
          pinnedBelowStackOuterHeightRef.current = baseline.stackOuterPx;
          setBelowStackPinnedOuterHeightPx(baseline.stackOuterPx);
        } else if (!isZoomScaleOne(zoomScale)) {
          centererWidth = Math.round(centererWidth * zoomScale);
          centererHeight = Math.round(centererHeight * zoomScale);
          widthToUse = Math.round(widthToUse * zoomScale);
          heightToUse = Math.round(heightToUse * zoomScale);
        }
      } else if (effectiveZoomLevel !== 0 || deferredFullscreenZoom.cssScaleBoost !== 1) {
        // Use visual (preview) scale so pending CSS boost and settled raster stay aligned.
        const zoomScale = deferredFullscreenZoom.previewScale;
        centererWidth = Math.round(centererWidth * zoomScale);
        centererHeight = Math.round(centererHeight * zoomScale);
        widthToUse = Math.round(widthToUse * zoomScale);
        heightToUse = Math.round(heightToUse * zoomScale);
      }

      setVideoLayoutStyles(
        {
          width: `${centererWidth}px`,
          // Fullscreen zoom may exceed the slot; keep layout size honest so the
          // top-anchored scrollport can reach the start of the frame.
          maxWidth: isFullscreen && (effectiveZoomLevel !== 0 || deferredFullscreenZoom.cssScaleBoost !== 1)
            ? 'none'
            : '100%',
          height: `${centererHeight}px`,
          flexShrink: 0,
          overflow: 'hidden',
        },
        {
          width: `${widthToUse}px`,
          height: `${heightToUse}px`,
          objectFit: 'contain',
          rotate: `${rotationDeg}deg`,
        },
      );
    }
  };

  const updateContainerDimensions = () => {
    if (!containerRef.current) return;
    const eventModalSideLayout =
      isEventModal &&
      !isFullscreen &&
      !isUnifiedSources &&
      showTranscriptState &&
      transcript.length > 0 &&
      !!transcriptSrc &&
      !useStillFrame &&
      layoutTranscriptPosition === 'side';
    if (
      mediaItem.mediaType !== 'video' &&
      !(isFullscreen && isAudioFile) &&
      !(eventModalSideLayout && isAudioFile)
    ) {
      return;
    }
    if (trimDragActiveRef.current) return;
    if (dimensionsLockRef.current) return;
    dimensionsLockRef.current = true;

    let adjustedWidth: number;
    let adjustedHeight: number;

    if (eventModalSideLayout) {
      adjustedWidth = EVENT_MODAL_SIDE_PLAYER_WIDTH_PX;
      if (fitHeight && fitHeight > 0) {
        adjustedHeight = Math.round(fitHeight);
      } else {
        const measured = measureMediaDisplayContainer(containerRef.current);
        adjustedHeight = Math.max(0, Math.round(measured.height));
      }
    } else if (isFullscreen) {
      const measured = measureMediaDisplayContainer(containerRef.current);
      adjustedWidth = measured.width;
      adjustedHeight = measureFullscreenPlayerBudgetPx(containerRef.current);
      if (adjustedHeight <= 0) {
        adjustedHeight = Math.round(
          measured.height - measureFullscreenToolbarHeightPx(containerRef.current),
        );
      }
    } else if (
      isPresentation ||
      (fitWidth != null && fitWidth > 0) ||
      (fitHeight != null && fitHeight > 0)
    ) {
      // Prefer a stable fit box (SourceCard / event-modal). Use whichever fit
      // dims are available — do not require both.
      const slot = containerRef.current.parentElement as HTMLElement | null;
      const slotRect = slot?.getBoundingClientRect();
      if (fitWidth && fitWidth > 0) {
        adjustedWidth = Math.round(fitWidth);
      } else if (slotRect && slotRect.width > 0) {
        adjustedWidth = Math.round(slotRect.width);
      } else {
        adjustedWidth = measureMediaDisplayContainer(containerRef.current).width;
      }
      if (fitHeight && fitHeight > 0) {
        adjustedHeight = Math.round(fitHeight);
      } else if (slotRect && slotRect.height > 0) {
        adjustedHeight = Math.round(slotRect.height);
      } else {
        const measured = measureMediaDisplayContainer(containerRef.current);
        adjustedHeight = Math.max(0, Math.round(measured.height));
      }
      if (sourcesExpanded && isPresentation) {
        adjustedHeight = Math.round(adjustedHeight * 0.8);
      }
    } else {
      const measured = measureMediaDisplayContainer(containerRef.current);
      adjustedWidth = measured.width;
      const ancestor = containerRef.current.closest('.mediaDisplay') as HTMLElement
        || containerRef.current.parentElement as HTMLElement
        || containerRef.current;
      const ancestorRect = ancestor.getBoundingClientRect();
      const topOffset = ancestorRect.top > 0 ? ancestorRect.top : 0;
      adjustedHeight = Math.round(window.innerHeight - topOffset - 160);
      if (adjustedHeight <= 0) adjustedHeight = Math.round(window.innerHeight * 0.6);
    }

    setContainerDimensions((prev) => {
      // Ignore sub-pixel / feedback-loop jitter from mediaDisplay ↔ video re-fit
      // (especially SourceCard, where the player sizes against a measured ancestor).
      if (
        Math.abs(prev.width - adjustedWidth) < 2 &&
        Math.abs(prev.height - adjustedHeight) < 2
      ) {
        return prev;
      }
      return { width: adjustedWidth, height: adjustedHeight };
    });

    requestAnimationFrame(() => {
      dimensionsLockRef.current = false;
    });
  };

  const trimDragWasActiveRef = useRef(false);
  useEffect(() => {
    if (trimDragActive) {
      trimDragWasActiveRef.current = true;
      return;
    }
    if (!trimDragWasActiveRef.current) return;
    trimDragWasActiveRef.current = false;
    handleLoadedMetadata();
    updateContainerDimensions();
  }, [trimDragActive]);

  const recomputeFullscreenTranscriptLayout = useCallback(() => {
    // SourceCard / Evidence Locker must NOT use this path — measuring
    // ``.mediaDisplay`` grows with content, so a second pass sets an oversized
    // transcript height and re-triggers metadata fit (transcript past popover).
    if (!isFullscreen || isTable || isAccordion || isUnifiedSources) {
      setFullscreenBelowTranscriptHeight(null);
      return;
    }

    const hasTranscript = showTranscriptState && transcript.length > 0 && !!transcriptSrc;
    if (!hasTranscript || isAudioFile || layoutTranscriptPosition !== 'below') {
      setFullscreenBelowTranscriptHeight(null);
      return;
    }

    let availableBudget = measureFullscreenPlayerBudgetPx(containerRef.current);
    if (availableBudget <= 0) {
      const { height } = measureMediaDisplayContainer(containerRef.current);
      availableBudget = height;
      if (height <= 0 && containerRef.current) {
        const mediaDisplay = containerRef.current.closest('.mediaDisplay') as HTMLElement | null;
        const top = mediaDisplay?.getBoundingClientRect().top ?? containerRef.current.getBoundingClientRect().top;
        availableBudget = Math.round(window.innerHeight - Math.max(top, 0) - 24);
      }
      if (availableBudget > 0) {
        availableBudget = Math.max(
          0,
          availableBudget - measureFullscreenToolbarHeightPx(containerRef.current),
        );
      }
    }
    if (availableBudget <= 0) {
      return;
    }

    if (
      effectiveZoomLevel !== 0
      || !isZoomScaleOne(deferredFullscreenZoom.previewScale)
    ) {
      return;
    }

    const chromePx = getFullscreenBelowChromePx(
      playerRef.current,
      videoCentererRef.current,
    );
    const available =
      availableBudget
      - BELOW_STACK_SPLITTER_TOTAL_PX
      - chromePx
      - FULLSCREEN_BELOW_STACK_SLACK_PX;
    if (belowStackSplitLockedRef.current) {
      return;
    }
    // Keep enough room for a usable video while guaranteeing the transcript is scrollable.
    const transcriptH = Math.max(
      TRANSCRIPT_MIN_HEIGHT_PX,
      Math.min(Math.round(available * 0.38), available - 120),
    );
    setFullscreenBelowTranscriptHeight(transcriptH);
  }, [
    isFullscreen,
    isTable,
    isAccordion,
    isUnifiedSources,
    isAudioFile,
    transcriptPosition,
    isNarrowForSideTranscript,
    showTranscriptState,
    transcript.length,
    transcriptSrc,
    effectiveZoomLevel,
    deferredFullscreenZoom.previewScale,
  ]);

  useEffect(() => {
    handleLoadedMetadata();
  }, [
    totalRotation,
    containerDimensions.width,
    containerDimensions.height,
    showTranscriptState,
    transcriptPosition,
    isNarrowForSideTranscript,
    sourcesExpanded,
    transcript.length,
    isFullscreen,
    isEventModal,
    isPresentation,
    fitWidth,
    fitHeight,
    effectiveZoomLevel,
    deferredFullscreenZoom.previewScale,
    fullscreenBelowTranscriptHeight,
    belowStackTranscriptPx,
    softBelowTranscriptPanelHeight,
    sideStackTranscriptPx,
  ]);

  useEffect(() => {
    recomputeFullscreenTranscriptLayout();
  }, [recomputeFullscreenTranscriptLayout, containerDimensions.height]);

  // New media identity — drop soft-budget pin / overflow ceiling.
  useEffect(() => {
    pinnedSoftBelowBudgetRef.current = null;
    pinnedSoftBelowBudgetWidthRef.current = 0;
    softBelowOverflowCeilRef.current = null;
    userGripBelowTranscriptPxRef.current = null;
    userGripSoftBelowBudgetPxRef.current = null;
    userGripBelowChromePxRef.current = null;
    // Safety net: a grip drag interrupted by unmount never reports its end.
    belowGripDragActiveRef.current = false;
    softBelowMetadataRefitCountRef.current = 0;
    setSoftBelowTranscriptPanelHeight(null);
    belowStackSplitLockedRef.current = false;
    pinnedBelowStackOuterHeightRef.current = null;
    belowStackDragTranscriptPxRef.current = null;
    belowStackSplitLayoutRef.current = null;
    belowStackZoomBaselineRef.current = null;
    belowStackZoomBaselineWidthRef.current = 0;
    pinnedPresentationPlayerOuterRef.current = null;
    setPinnedPresentationPlayerOuterPx(null);
    setBelowStackTranscriptPx(null);
    setBelowStackPinnedOuterHeightPx(null);
    sideStackSplitLockedRef.current = false;
    pinnedSideStackOuterWidthRef.current = null;
    sideStackRowGapPxRef.current = SIDE_STACK_SPLITTER_TOTAL_PX;
    sideStackDragTranscriptPxRef.current = null;
    sideStackSplitLayoutRef.current = null;
    setSideStackTranscriptPx(null);
    setSideStackPinnedOuterWidthPx(null);
    setAudioSideSplitRatio(null);
    setAudioSideDragMinHeightPx(null);
  }, [usableMediaSrc, transcriptSrc]);

  // Evidence Locker scrollports: refit when placement / dialog height settles.
  useEffect(() => {
    if (!isPresentation || isFullscreen) return;
    const container = containerRef.current;
    if (!container) return;
    const scrollport = container.closest(
      '[data-source-card-scrollport]',
    ) as HTMLElement | null;
    if (
      !scrollport
      || (!isEvidenceLockerScrollport(scrollport)
        && !isEvidenceLockerOverlayScrollport(scrollport))
    ) {
      return;
    }

    let debounceId: ReturnType<typeof setTimeout>;
    const refit = () => {
      // Zoom owns the below-stack split; scrollport remasure while zoomed restarts the
      // soft-fit ↔ chrome feedback loop that jitters the transcript bottom edge.
      if (!isZoomScaleOne(deferredFullscreenZoom.previewScale)) {
        return;
      }
      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        handleLoadedMetadata();
      }, 50);
    };

    const ro = new ResizeObserver(refit);
    ro.observe(scrollport);
    let node: HTMLElement | null = scrollport.parentElement;
    while (node && node !== document.documentElement) {
      const maxH = Number.parseFloat(getComputedStyle(node).maxHeight);
      if (Number.isFinite(maxH) && maxH > 0) {
        ro.observe(node);
      }
      node = node.parentElement;
    }

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(refit);
    });

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(debounceId);
      ro.disconnect();
    };
  }, [
    isPresentation,
    isFullscreen,
    usableMediaSrc,
    transcriptSrc,
    showTranscriptState,
    layoutTranscriptPosition,
    deferredFullscreenZoom.previewScale,
  ]);

  // Evidence Locker popover only: if the handle paints past PopoverContent, tighten the
  // overflow ceiling and refit (including when the natural-size path skipped
  // soft-fit). Never run for AnswerItem.
  useLayoutEffect(() => {
    if (!isPresentation || isFullscreen) return;
    // Zoom-out pins the stack; overflow correction would remasure chrome and fight it.
    if (!isZoomScaleOne(deferredFullscreenZoom.previewScale)) return;
    const container = containerRef.current;
    const panel = transcriptRef.current;
    if (!container || !panel) return;

    const scrollport = container.closest(
      '[data-source-card-scrollport]',
    ) as HTMLElement | null;
    if (!isEvidenceLockerScrollport(scrollport)) return;

    const popoverParent = scrollport!.parentElement;
    if (!popoverParent) return;
    const parentBottom = popoverParent.getBoundingClientRect().bottom;
    const card = container.closest('[data-testid="SourceCard"]') as HTMLElement | null;
    const padBottom = card
      ? Number.parseFloat(getComputedStyle(card).paddingBottom) || 0
      : 0;
    const clipBottom = parentBottom - padBottom;
    const overflow = Math.ceil(panel.getBoundingClientRect().bottom - clipBottom);
    if (overflow <= LAYOUT_HEIGHT_HYSTERESIS_PX) return;

    const currentBudget =
      softBelowOverflowCeilRef.current ??
      measureSoftBelowTranscriptBudget(container);
    if (!(currentBudget > 0)) return;
    const minStack =
      SOFT_BELOW_TRANSCRIPT_MIN_PX +
      SOFT_BELOW_VIDEO_MIN_PX +
      PRESENTATION_PLAYER_CHROME_RESERVE_PX;
    const next = Math.max(minStack, currentBudget - overflow);
    const prevCeil = softBelowOverflowCeilRef.current;
    if (prevCeil != null && next >= prevCeil) return;
    softBelowOverflowCeilRef.current = next;
    handleLoadedMetadata();
  }, [
    isPresentation,
    isFullscreen,
    softBelowTranscriptPanelHeight,
    videoCentererStyle?.height,
    videoCentererStyle?.width,
    showTranscriptState,
    layoutTranscriptPosition,
    deferredFullscreenZoom.previewScale,
  ]);

  useEffect(() => {
    if (!isTable && !isAccordion && !isUnifiedSources) {
      requestAnimationFrame(() => {
        dimensionsLockRef.current = false;
        updateContainerDimensions();
        recomputeFullscreenTranscriptLayout();
      });
    }
  }, [
    sourcesExpanded,
    isFullscreen,
    isAudioFile,
    mediaItem.mediaType,
    isPresentation,
    isEventModal,
    fitWidth,
    fitHeight,
    transcriptPosition,
    isNarrowForSideTranscript,
    showTranscriptState,
    recomputeFullscreenTranscriptLayout,
  ]);

  useEffect(() => {
    if ((mediaItem.mediaType !== 'video' && !(isFullscreen && isAudioFile)) || isTable || isAccordion || isUnifiedSources) {
      return;
    }

    let debounceId: NodeJS.Timeout;
    const onResize = () => {
      if (trimDragActiveRef.current) return;
      if (belowStackSplitDragActiveRef.current) return;
      if (sideStackSplitDragActiveRef.current) return;
      if (belowGripDragActiveRef.current) return;
      clearTimeout(debounceId);
      dimensionsLockRef.current = false;
      debounceId = setTimeout(() => {
        updateContainerDimensions();
        recomputeFullscreenTranscriptLayout();
      }, 200);
    };

    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(debounceId);
      window.removeEventListener('resize', onResize);
    };
    // Depend on media identity fields — not the whole mediaItem — so trim-handle
    // edits (new object identity every mousemove) do not rebind listeners.
  }, [
    mediaItem.mediaType,
    isTable,
    isAccordion,
    isUnifiedSources,
    isPresentation,
    isFullscreen,
    isAudioFile,
    recomputeFullscreenTranscriptLayout,
  ]);

  useEffect(() => {
    if ((mediaItem.mediaType !== 'video' && !(isFullscreen && isAudioFile)) || isTable || isAccordion || isUnifiedSources) {
      return;
    }
    // Answer-item SourceCard: ``.mediaDisplay`` is content-sized — observing it re-runs
    // fit on every shrink and tightens the soft budget (feedback loop). Evidence Locker
    // and presentation use stable outer slots instead.
    const answerItemScrollport = containerRef.current?.closest(
      '[data-source-card-scrollport]',
    ) as HTMLElement | null;
    if (
      answerItemScrollport
      && !isEvidenceLockerScrollport(answerItemScrollport)
    ) {
      return;
    }
    // Presentation/SourceCard: observe the player slot (stable pinned size).
    // Elsewhere observe mediaDisplay / fullscreen inner.
    const ancestor = isPresentation
      ? (containerRef.current?.parentElement as HTMLElement | null)
      : ((containerRef.current?.closest('.mediaDisplay') as HTMLElement | null)
        || document.getElementById('fullScreenMediaDisplayInner'));
    if (!ancestor) return;

    const fullscreenToolbar = isFullscreen
      ? (containerRef.current?.closest('.mediaDisplay')
          ?.querySelector('.mediaDisplayControls') as HTMLElement | null)
      : null;
    const fullscreenPlayerSlot = isFullscreen
      ? (containerRef.current?.closest('#media-player-container-fullscreen') as HTMLElement | null)
      : null;

    let debounceId: NodeJS.Timeout;
    const observer = new ResizeObserver(() => {
      if (trimDragActiveRef.current) return;
      if (belowStackSplitDragActiveRef.current) return;
      if (sideStackSplitDragActiveRef.current) return;
      if (belowGripDragActiveRef.current) return;
      clearTimeout(debounceId);
      dimensionsLockRef.current = false;
      debounceId = setTimeout(() => {
        updateContainerDimensions();
        recomputeFullscreenTranscriptLayout();
      }, 200);
    });
    observer.observe(ancestor);
    if (fullscreenToolbar) {
      observer.observe(fullscreenToolbar);
    }
    if (fullscreenPlayerSlot && fullscreenPlayerSlot !== ancestor) {
      observer.observe(fullscreenPlayerSlot);
    }

    return () => {
      clearTimeout(debounceId);
      observer.disconnect();
    };
  }, [
    containerDimensions.width,
    mediaItem.mediaType,
    isTable,
    isAccordion,
    isUnifiedSources,
    isFullscreen,
    isPresentation,
    isAudioFile,
    recomputeFullscreenTranscriptLayout,
  ]);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const setAudioData = () => {
      setDuration(media.duration);
    };

    const setAudioTime = () => {
      window.dispatchEvent(new Event('tp-media-playing'));
      setCurrentTime(media.currentTime);
      
      // Only pause at end point if playback started before the end point and user hasn't manually overridden
      if (!isEditingMediaContext && 
          mediaItem?.mediaContext?.endTime &&
          !hasManuallyOverriddenEndTime) {
        const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
        if (media.currentTime >= endTimeSeconds) {
          media.pause();
          setIsPlaying(false);
        }
      }
    };

    // Events
    media.addEventListener('loadedmetadata', setAudioData);
    media.addEventListener('timeupdate', setAudioTime);
    media.addEventListener('ended', () => {
      if (!isEditingMediaContext && 
          mediaItem?.mediaContext?.endTime &&
          !hasManuallyOverriddenEndTime) {
        const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
        if (media.currentTime >= endTimeSeconds) {
          setIsPlaying(false);
        }
      }
    });
    return () => {
      media.removeEventListener('loadedmetadata', setAudioData);
      media.removeEventListener('timeupdate', setAudioTime);
      media.removeEventListener('ended', () => {});
    };
  }, [usableMediaSrc, isEditingMediaContext, mediaItem?.mediaContext?.endTime, hasManuallyOverriddenEndTime]);

  // Track the element itself rather than only our own controls: playback also
  // stops from outside this component (``pauseMediaElements`` when the user
  // leaves Assistant, OS/browser media keys, a rejected ``play()``). Without
  // this the control would still read "pause" on stopped media, so the next
  // click would just flip the flag instead of resuming.
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const syncPlayState = () => setIsPlaying(!media.paused);

    syncPlayState();
    media.addEventListener('play', syncPlayState);
    media.addEventListener('pause', syncPlayState);

    return () => {
      media.removeEventListener('play', syncPlayState);
      media.removeEventListener('pause', syncPlayState);
    };
  }, [usableMediaSrc]);

  // Reset override state when entering/exiting edit mode
  useEffect(() => {
    setHasManuallyOverriddenEndTime(false);
  }, [isEditingMediaContext]);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const updateCurrentTime = () => {
      setCurrentTime(media.currentTime);
      requestAnimationFrame(updateCurrentTime);
    };

    requestAnimationFrame(updateCurrentTime);

    return () => {

    };
  }, [playbackRate]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTranscript(transcript);
    } else {
      const lowerCaseQuery = searchQuery.toLowerCase();
      const filtered = transcript.filter(item =>
        item.text.toLowerCase().includes(lowerCaseQuery)
      );
      setFilteredTranscript(filtered);
    }
  }, [searchQuery, transcript]);

  useEffect(() => {
    // Update active segment based on current time
    const newActiveIndex = filteredTranscript.findIndex(
      (item) => currentTime >= item.start && currentTime <= item.end
    );

    if (newActiveIndex !== activeSegmentIndex) {
      setActiveSegmentIndex(newActiveIndex);
    }
  }, [currentTime, filteredTranscript, activeSegmentIndex]);

  useEffect(() => {
    if (disableHotkeys) return;

    let scrubbingInterval: NodeJS.Timeout | null = null;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore hotkeys if any input element or markdown editor has focus
      if (isEditingMediaContext ||
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement ||
          event.target instanceof HTMLSelectElement ||
          (event.target instanceof HTMLElement && event.target.closest('.mdxeditor-root-contenteditable')) ||
          (event.target instanceof HTMLElement && event.target.closest('#feedback-button'))) {
        return;
      }

      if (event.shiftKey && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
        event.preventDefault();
        event.stopPropagation();

        const scrubAmount = 1; // Scrub distance in seconds

        if (scrubbingInterval) return;

        scrubbingInterval = setInterval(() => {
          if (mediaRef.current) {
            if (event.key === 'ArrowRight') {
              mediaRef.current.currentTime = Math.min(mediaRef.current.currentTime + scrubAmount, duration);
            } else if (event.key === 'ArrowLeft') {
              mediaRef.current.currentTime = Math.max(mediaRef.current.currentTime - scrubAmount, 0);
            }
            setCurrentTime(mediaRef.current.currentTime);
          }
        }, 200); // Scrub every 200ms for smoother effect
      } else if (!event.shiftKey) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveToNextSegment();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveToPreviousSegment();
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (scrubbingInterval && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
        clearInterval(scrubbingInterval);
        scrubbingInterval = null;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (scrubbingInterval) {
        clearInterval(scrubbingInterval);
      }
    };
  }, [duration, filteredTranscript, activeSegmentIndex, disableHotkeys]);

  const moveToNextSegment = () => {
    let nextIndex = filteredTranscript.findIndex(
      (item, index) => index > activeSegmentIndex && item.start > currentTime
    );

    if (nextIndex === -1 && activeSegmentIndex === -1) {
      nextIndex = filteredTranscript.findIndex(item => item.start > currentTime);
    }

    if (nextIndex !== -1) {
      setActiveSegmentIndex(nextIndex);
      handleSegmentClick(filteredTranscript[nextIndex].start);

      if (transcriptRef.current) {
        const el = transcriptRef.current.children[nextIndex] as HTMLElement;
        if (el) {
          const container = transcriptRef.current;
          const elRect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (elRect.top < containerRect.top || elRect.bottom > containerRect.bottom) {
            container.scrollTo({
              top: el.offsetTop - container.offsetTop - (container.clientHeight / 2) + (el.clientHeight / 2),
              behavior: 'smooth',
            });
          }
        }
      }
    } else if (activeSegmentIndex === filteredTranscript.length - 1) {
      if (mediaRef.current) {
        mediaRef.current.currentTime = duration;
        setCurrentTime(duration);
      }
    }
  };

  const moveToPreviousSegment = () => {
    let prevIndex = -1;

    if (activeSegmentIndex !== -1) {
      prevIndex = filteredTranscript.slice(0, activeSegmentIndex).reverse().findIndex(
        (item) => item.end < currentTime
      );
      if (prevIndex !== -1) {
        prevIndex = activeSegmentIndex - 1 - prevIndex;
      }
    } else {
      prevIndex = filteredTranscript.slice().reverse().findIndex(
        (item) => item.end < currentTime
      );
      if (prevIndex !== -1) {
        prevIndex = filteredTranscript.length - 1 - prevIndex;
      }
    }

    if (prevIndex !== -1) {
      setActiveSegmentIndex(prevIndex);
      handleSegmentClick(filteredTranscript[prevIndex].start);

      if (transcriptRef.current) {
        const el = transcriptRef.current.children[prevIndex] as HTMLElement;
        if (el) {
          const container = transcriptRef.current;
          const elRect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (elRect.top < containerRect.top || elRect.bottom > containerRect.bottom) {
            container.scrollTo({
              top: el.offsetTop - container.offsetTop - (container.clientHeight / 2) + (el.clientHeight / 2),
              behavior: 'smooth',
            });
          }
        }
      }
    } else if (activeSegmentIndex === 0) {
      if (mediaRef.current) {
        mediaRef.current.currentTime = 0;
        setCurrentTime(0);
      }
    }
  };

  const togglePlay = () => {
    if (!mediaRef.current) return;

    if (!mediaRef.current.paused) {
      mediaRef.current.pause(); 
    } else {
      // If in edit mode, start from the start time indicator position
      if (isEditingMediaContext && mediaItem?.mediaContext?.startTime) {
        const startTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.startTime);
        mediaRef.current.currentTime = startTimeSeconds;
        setCurrentTime(startTimeSeconds);
      }
      
      // Play at/past end → allow continuing past the trim. Play from before end →
      // re-arm pause-at-end even if a prior override was still set.
      if (!isEditingMediaContext && mediaItem?.mediaContext?.endTime) {
        const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
        if (mediaRef.current.currentTime >= endTimeSeconds) {
          setHasManuallyOverriddenEndTime(true);
        } else {
          setHasManuallyOverriddenEndTime(false);
        }
      }
      
      // If current time is before the end point, playback will stop at end point
      // If current time is after the end point, playback will continue normally
      mediaRef.current.play();
    }
  };

  const toggleMute = () => {
    if (!mediaRef.current) return;
    
    if (isMuted) {
      mediaRef.current.volume = volume;
    } else {
      mediaRef.current.volume = 0;
    }
    setIsMuted(!isMuted);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mediaRef.current) return;
    
    const time = parseFloat(e.target.value);
    mediaRef.current.currentTime = time;
    setCurrentTime(time);
    
    // Reset override state if seeking before end time
    if (hasManuallyOverriddenEndTime && mediaItem?.mediaContext?.endTime) {
      const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
      if (time < endTimeSeconds) {
        setHasManuallyOverriddenEndTime(false);
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mediaRef.current) return;  
    
    const newVolume = parseFloat(e.target.value);
    mediaRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleSegmentClick = (startTime: number) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = startTime;
      setCurrentTime(startTime);
      
      // Reset override state if seeking before end time
      if (hasManuallyOverriddenEndTime && mediaItem?.mediaContext?.endTime) {
        const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
        if (startTime < endTimeSeconds) {
          setHasManuallyOverriddenEndTime(false);
        }
      }
    }
  };

  const handlePlaybackRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRate = parseFloat(e.target.value);
    setPlaybackRate(newRate);
    if (mediaRef.current) {
      mediaRef.current.playbackRate = newRate;
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mediaRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const newTime = (percentage / 100) * duration;
    setHoverTime(newTime);
  };

  const handleMouseLeave = () => {
    setHoverTime(null);
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>, startTime?: number, updateMediaContext?: (field: string, value: any) => void, mediaItem?: any) => {
    if (!mediaRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const newTime = (percentage / 100) * duration;

    // If in edit mode and clicking to the left of the start indicator, move the start indicator
    if (isEditingMediaContext && startTime !== undefined && newTime < startTime) {
      if (updateMediaContext) {
        updateMediaContext('startTime', Number(newTime.toFixed(2)));
      }
      return;
    }

    mediaRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    
    // Reset override state if seeking before end time
    if (hasManuallyOverriddenEndTime && mediaItem?.mediaContext?.endTime) {
      const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
      if (newTime < endTimeSeconds) {
        setHasManuallyOverriddenEndTime(false);
      }
    }
  };

  const toggleTranscriptMarkersVisibility = () => {
    const newState = !showTranscriptMarkersState;
    setShowTranscriptMarkersState(newState);
    if (updateMediaContext) {
      updateMediaContext('showTranscriptMarkers', newState);
    }
  };

  const toggleTranscriptVisibility = () => {
    const newState = !showTranscriptState;
    setShowTranscriptState(newState);
    if (updateMediaContext) {
      updateMediaContext('showTranscript', newState);
    }
  };

  const setStillFrame = async () => {
    if (!updateMediaContext || !(mediaRef.current instanceof HTMLVideoElement)) {
      return;
    }

    const video = mediaRef.current;
    const currentFrameTime = video.currentTime;
    let image: string | null = null;

    try {
      image = captureVideoFrame(video);
    } catch (error) {
      if (!(error instanceof DOMException) || error.name !== 'SecurityError') {
        throw error;
      }
    }

    if (!image && sameOriginCaptureSrc) {
      let captureVideo: HTMLVideoElement | null = null;
      try {
        captureVideo = await loadSameOriginVideoFrame(sameOriginCaptureSrc, currentFrameTime);
        image = captureVideoFrame(captureVideo);
      } catch (error) {
        console.warn(
          'Failed to capture still frame through same-origin proxy:',
          error instanceof Error ? error.message : error,
        );
      } finally {
        detachCaptureVideo(captureVideo);
      }
    }

    if (!image) {
      return;
    }

    const stillFrameTime = currentFrameTime.toFixed(0);
    updateMediaContext('stillFrame', stillFrameTime);

    const cellId = resolveCellIdFromIdToken(idToken);
    const { sourceKey, sourceMd5 } = originBindingFromMedia(mediaItem);
    const caseIdForUpload = resolveUserImageUploadCaseId({
      selectedCaseId: sharedCaseData?.caseId,
      media: mediaItem,
      sourceKey,
    });
    if (!cellId || !realAccessToken || !caseIdForUpload) {
      updateMediaContext({
        stillFrame: Number(currentFrameTime.toFixed(2)),
        canvas: {
          ...mediaItem.mediaContext?.canvas,
          stillFrameImage: image,
          stillFrame: stillFrameTime,
        },
      });
      return;
    }

    try {
      const { s3Uri } = await uploadUserImage({
        imageData: image,
        mimeType: 'image/webp',
        purpose: 'video-still-frame',
        caseId: caseIdForUpload,
        tenantFriendlyName:
          sharedCaseData?.tenantFriendlyName ??
          (typeof tenantFriendlyNameFlag === 'string'
            ? tenantFriendlyNameFlag
            : undefined),
        sourceMd5,
        sourceKey,
        cellId,
        accessToken: realAccessToken,
      });
      updateMediaContext({
        stillFrame: stillFrameTime,
        canvas: {
          ...mediaItem.mediaContext?.canvas,
          stillFrameImage: s3Uri,
          stillFrame: stillFrameTime,
        },
      });
    } catch (err) {
      console.error('Failed to capture or upload still frame', err);
      updateMediaContext({
        stillFrame: Number(currentFrameTime.toFixed(2)),
        canvas: {
          ...mediaItem.mediaContext?.canvas,
          stillFrameImage: image,
          stillFrame: stillFrameTime,
        },
      });
    }
  };

  const clearStillFrame = () => {
    if (!updateMediaContext) {
      return;
    }
    updateMediaContext({
      stillFrame: undefined,
      canvas: {
        ...mediaItem.mediaContext?.canvas,
        stillFrame: undefined,
        stillFrameImage: undefined,
      },
    });
  };

  const handleSetStartTime = () => {
    if (mediaItem?.mediaContext && updateMediaContext) {
      const newStartTime = currentTime;
      const newEndTime = mediaItem.mediaContext.endTime;
      updateMediaContext('startTime', newStartTime);
      updateMediaContext('endTime', newEndTime);
    }
  };

  const handleSetEndTime = () => {
    if (mediaItem?.mediaContext && updateMediaContext) {
      const newStartTime = mediaItem.mediaContext.startTime;
      const newEndTime = currentTime;
      updateMediaContext('startTime', newStartTime);
      updateMediaContext('endTime', newEndTime);
    }
  };

  const handleUpdateMediaContext = (field: string, value: any) => {
    // Do not require mediaContext to already exist — trim edits from fullscreen must
    // still reach the owner even when context is being initialized.
    if (updateMediaContext) {
      updateMediaContext(field, value);
    }
  };

  const toggleMenuVisibility = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuVisible(false);
      }
    };

    if (!isTable && !isAccordion && !isUnifiedSources) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTable, isAccordion, isUnifiedSources]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchQuery = e.target.value;
    setSearchQuery(newSearchQuery);
    
    // If search is being cleared and transcripts were initially hidden
    if (newSearchQuery === '' && !showTranscript) {
      setShowTranscriptState(false);
      if (updateMediaContext) {
        updateMediaContext('showTranscript', false);
      }
    }
  };

  const toggleSearchInputVisibility = () => {
    setIsSearchInputVisible(prevState => !prevState);
  };

  useEffect(() => {
    if (isSearchInputVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchInputVisible]);

  const changeTranscriptPosition = (position: 'side' | 'below') => {
    setTranscriptPosition(position);
    if (updateMediaContext) {
      updateMediaContext('transcriptPosition', position);
    }
  };

  // Function to get the current height of the player
  const getPlayerHeight = () => {
    // Prefer the ResizeObserver-driven measurement (see effect above) — it reflects
    // the player's height as of its last real layout change, whereas a plain
    // clientHeight read during render can be one commit stale right after the video's
    // post-metadata resize (TR-1589 follow-up).
    if (measuredPlayerHeight > 0) {
      return measuredPlayerHeight;
    }
    if (playerRef.current) {
      return playerRef.current.clientHeight;
    }
    return 0;
  };

  // Function to get the current width of the player
  const getPlayerWidth = () => {
    if (playerRef.current) {
      return playerRef.current.clientWidth;
    }
    return 0;
  };

  const handleLoadStart = () => {
    setIsLoading(true);
  };

  const handleLoadedData = () => {
    refreshAttemptsRef.current = 0;
    setIsLoading(false);
    applyConfiguredStartTime();
  };

  const handleMediaTagError = async (e: React.SyntheticEvent<HTMLVideoElement | HTMLAudioElement>) => {
    const target = e.currentTarget;
    const error = target.error;

    // Most likely cause of an error after we were already playing OK is a
    // presigned-URL TTL expiry: S3 starts returning 403 mid-stream once the
    // signature window closes. Re-mint and call .load() to retry transparently
    // up to MAX_PRESIGN_REFRESH_ATTEMPTS times before falling through to the
    // error logging below. The counter is reset by handleLoadedData on a
    // successful (re-)load.
    //
    // ``flushSync`` is load-bearing: ``target.load()`` reads the DOM ``src``
    // attribute synchronously, so we must commit the new ``presignedUrl``
    // (and therefore the recomputed ``usableMediaSrc`` bound to ``src`` in
    // JSX) before calling it. Without flushSync, React batches the render
    // until after the current event handler returns; ``.load()`` then
    // re-fetches the still-expired URL, fires another error, and we burn
    // MAX_PRESIGN_REFRESH_ATTEMPTS without ever actually trying the fresh
    // URL.
    if (refreshAttemptsRef.current < MAX_PRESIGN_REFRESH_ATTEMPTS) {
      refreshAttemptsRef.current += 1;
      const fresh = await refreshPresignedUrl();
      if (fresh) {
        flushSync(() => setPresignedUrl(fresh));
        target.load();
        return;
      }
    }

    const errorDetails = {
      code: error?.code,
      message: error?.message,
      errorName: error ? ['MEDIA_ERR_ABORTED', 'MEDIA_ERR_NETWORK', 'MEDIA_ERR_DECODE', 'MEDIA_ERR_SRC_NOT_SUPPORTED'][error.code - 1] : 'Unknown',
      src: target.src,
      networkState: target.networkState,
      readyState: target.readyState,
      currentSrc: target.currentSrc,
      refreshAttempts: refreshAttemptsRef.current,
    };

    console.error('🎥 Media Error:', errorDetails);
    console.error('Full error object:', error);
    console.error('Target element:', target);
  };

  useEffect(() => {
    if (layoutTranscriptPosition !== 'below' || !transcriptRef.current) {
      return;
    }
    // Event-modal below stack fills the Selected Media fit box (see
    // getTranscriptLayoutStyle); do not shrink it back to the video frame width.
    if (isEventModal) {
      transcriptRef.current.style.width = '100%';
      return;
    }
    const playerWidth = getPlayerWidth();
    transcriptRef.current.style.width = `${playerWidth}px`;
  }, [layoutTranscriptPosition, getPlayerWidth, isEventModal]);

  const toggleAutoScroll = () => {
    setAutoScroll(!autoScroll);
    if (updateMediaContext) {
      updateMediaContext('autoScroll', !autoScroll);
    }
  };

  const checkStillFrameSize = () => {
    if (stillFrameRef.current) {
      const stillFrame = stillFrameRef.current as HTMLImageElement;
      const width = stillFrame.clientWidth;
      const height = stillFrame.clientHeight;
      if (totalRotation === 90 || totalRotation === -90 || totalRotation === 270) {
        setStillFrameCentererStyle({
          width: `${height}px`,
          height: `${width}px`,
          overflow: 'hidden',
        });
      }
    }
  };

  const canRenderStillFrameThumbnail = !isAudioFile && (isUnifiedSources || isAccordion || isTable) && !!stillFrameImage;
  const isGoingToRenderSetStillFramePrompt = !isAudioFile && (isUnifiedSources || isTable) && !stillFrameImage && !isAccordion;
  const isCompactThumbnail = !isFullscreen && (isUnifiedSources || isAccordion || isTable) && !isHorizontalTimeline;
  const hasVisibleTranscript =
    transcript.length > 0 && !!transcriptSrc && showTranscriptState && !useStillFrame && !isUnifiedSources;
  // `transcriptPosition` is already resolved upstream in mediaDisplay.tsx via
  // resolveTranscriptPosition(): a saved mediaContext.transcriptPosition always
  // wins, and beside is the default when the user has never chosen a position.
  // `layoutTranscriptPosition` (declared earlier, near isNarrowForSideTranscript)
  // additionally collapses to 'below' when the container is too narrow for side by
  // side. Do not re-force 'below' unconditionally here — that silently overrode an
  // explicit 'side' choice in presentation mode (TR-1589 follow-up).
  const useEventModalSideLayout =
    isEventModal &&
    !isFullscreen &&
    hasVisibleTranscript &&
    layoutTranscriptPosition === 'side';
  const useFullscreenAudioLayout = isFullscreen && isAudioFile && hasVisibleTranscript;
  const useFullscreenVideoTranscriptLayout = isFullscreen && !isAudioFile && hasVisibleTranscript;
  const useBelowTranscriptLayout =
    hasVisibleTranscript && layoutTranscriptPosition === 'below' && !isCompactThumbnail;

  const useSideTranscriptLayout =
    hasVisibleTranscript
    && layoutTranscriptPosition === 'side'
    && !isCompactThumbnail
    && !useEventModalSideLayout;

  const sideLayoutRowGap = useSideTranscriptLayout ? 'gap-0' : 'gap-4';

  const activeBelowStackSplitTranscriptPx =
    belowStackSplitDragging && belowStackDragTranscriptPxRef.current != null
      ? belowStackDragTranscriptPxRef.current
      : belowStackTranscriptPx;

  const activeSideStackSplitTranscriptPx =
    sideStackSplitDragging && sideStackDragTranscriptPxRef.current != null
      ? sideStackDragTranscriptPxRef.current
      : sideStackTranscriptPx;

  const pinnedSidePlayerWidthPx =
    useSideTranscriptLayout
    && sideStackPinnedOuterWidthPx != null
    && activeSideStackSplitTranscriptPx != null
      ? sideStackPinnedOuterWidthPx
        - sideStackRowGapPxRef.current
        - activeSideStackSplitTranscriptPx
      : null;

  const sideSplitTranscriptStyle = (
    heightPx?: number,
  ): React.CSSProperties | null => {
    if (activeSideStackSplitTranscriptPx == null || activeSideStackSplitTranscriptPx <= 0) {
      return null;
    }
    const w = activeSideStackSplitTranscriptPx;
    const style: React.CSSProperties = {
      flex: '0 0 auto',
      width: `${w}px`,
      minWidth: w,
      maxWidth: `${w}px`,
      minHeight: 0,
      alignSelf: 'stretch',
      overflow: 'hidden',
    };
    if (heightPx != null && heightPx > 0) {
      style.height = `${heightPx}px`;
      style.maxHeight = `${heightPx}px`;
    } else {
      style.height = '100%';
      style.maxHeight = 'none';
    }
    return style;
  };

  const fullscreenLayoutZoomActive =
    isFullscreen
    && (effectiveZoomLevel !== 0 || deferredFullscreenZoom.cssScaleBoost !== 1);
  // TR-3394: narrower than fullscreenLayoutZoomActive — true only when the applied
  // scale can actually exceed 100% (zoom IN), not for zoom-out (effectiveZoomLevel
  // < 0, scale < 1) or the resting/settled state. A zoomed-out video is smaller
  // than its row and can never overflow it, so it doesn't need the
  // justify-start + overflow-auto "reachable scrollport" handling below — using
  // the broader fullscreenLayoutZoomActive there incorrectly left-hugged a
  // shrunk portrait video with even more leftover row slack than the unzoomed case.
  const fullscreenZoomMayOverflowRow =
    fullscreenLayoutZoomActive
    && (deferredFullscreenZoom.previewScale > 1 || deferredFullscreenZoom.layoutScale > 1);

  const getTranscriptLayoutStyle = (): React.CSSProperties => {
    // Same box before and during a drag. Switching height, flex-basis, or
    // min-width on the first pointer-move was resizing both columns vertically.
    if (isAudioFile && layoutTranscriptPosition === 'side' && !useEventModalSideLayout) {
      const matchedHeight = !isFullscreen ? getPlayerHeight() : 0;
      const sideHeightPx = matchedHeight > 0 ? matchedHeight : TRANSCRIPT_MIN_HEIGHT_PX;
      return {
        flex: `${1 - (audioSideSplitRatio ?? 0.5)} 1 0%`,
        width: 'auto',
        minWidth: SIDE_TRANSCRIPT_MIN_WIDTH_PX,
        maxWidth: 'none',
        alignSelf: 'stretch',
        overflow: 'hidden',
        minHeight: 0,
        ...(isFullscreen
          ? { height: '100%', maxHeight: 'none' }
          : { height: `${sideHeightPx}px`, maxHeight: `${sideHeightPx}px` }),
      };
    }

    if (!isFullscreen) {
      if (useEventModalSideLayout) {
        return {
          width: `${EVENT_MODAL_SIDE_TRANSCRIPT_WIDTH_PX}px`,
          flex: `0 0 ${EVENT_MODAL_SIDE_TRANSCRIPT_WIDTH_PX}px`,
          minWidth: EVENT_MODAL_SIDE_TRANSCRIPT_WIDTH_PX,
          maxWidth: EVENT_MODAL_SIDE_TRANSCRIPT_WIDTH_PX,
          minHeight: 0,
          height: 'auto',
          alignSelf: 'stretch',
          overflow: 'hidden',
        };
      }
      // SourceCard: definite height owned by Transcript (drag-resize). Do not pass
      // height:'auto' — that collapses the flex-1 body so the first line / handle vanish.
      if (isPresentation) {
        if (layoutTranscriptPosition === 'side') {
          const matchedHeight = getPlayerHeight();
          const sideHeightPx = matchedHeight > 0 ? matchedHeight : TRANSCRIPT_MIN_HEIGHT_PX;
          const splitStyle = sideSplitTranscriptStyle(sideHeightPx);
          if (splitStyle) {
            return splitStyle;
          }
          // Mirrors the generic (non-presentation) side style below: share the row
          // with the player via flex-basis 0 instead of stacking full-width.
          //
          // A *definite* pixel height (matched to the actual rendered player block —
          // video + Track + Controls, via getPlayerHeight()) instead of height:'auto'
          // + align-self:'stretch'. Presentation/SourceCard deliberately keep their
          // outer wrappers auto-height / overflow-visible so a below-stacked
          // transcript can grow the whole slide (see mediaDisplay.tsx's
          // `presentationLayout` styling: 'h-auto min-h-min shrink-0 overflow-visible').
          // That means there is no definite ancestor height for align-self:'stretch'
          // to resolve against here — 'auto' fell back to the transcript's own full,
          // unclipped content height, so the panel just kept growing (and blew past
          // the slide's fixed/overflow-hidden slot) instead of getting a bounded,
          // independently-scrollable viewport. That's why the transcript couldn't be
          // scrolled and never followed the video during playback — there was nothing
          // to scroll (TR-1589 follow-up).
          return {
            flex: '1 1 0',
            minWidth: 245,
            // Same reasoning as the isFullscreen side branch below: a portrait/
            // tall-aspect video's computed width can shrink well past "row width −
            // reserve" once the height-cap in handleLoadedMetadata kicks in, and this
            // flex-1 transcript would otherwise claim all of that freed-up row space.
            // Rob wants the video to always read as the bigger element (TR-1589
            // follow-up).
            maxWidth: '42%',
            height: `${sideHeightPx}px`,
            maxHeight: `${sideHeightPx}px`,
            overflow: 'hidden',
          };
        }
        // Soft scrollport fit (answer-item / Evidence Locker) passes a definite
        // height so the panel cannot extend past the first viewport. Between-panel
        // split drag locks a fixed share. Otherwise height comes from Transcript
        // bottom drag-resize state and SourceCard grows.
        if (activeBelowStackSplitTranscriptPx != null && activeBelowStackSplitTranscriptPx > 0) {
          const h = activeBelowStackSplitTranscriptPx;
          return {
            width: '100%',
            flex: '0 0 auto',
            alignSelf: 'stretch',
            height: `${h}px`,
            minHeight: h,
            maxHeight: `${h}px`,
            overflow: 'hidden',
          };
        }
        if (softBelowTranscriptPanelHeight != null && softBelowTranscriptPanelHeight > 0) {
          const h = getEffectiveBelowTranscriptHeight();
          return {
            width: '100%',
            flex: '0 0 auto',
            alignSelf: 'stretch',
            height: `${h}px`,
            minHeight: h,
            maxHeight: `${h}px`,
            overflow: 'hidden',
          };
        }
        return {
          width: '100%',
          flex: '0 0 auto',
          alignSelf: 'stretch',
          minHeight: TRANSCRIPT_MIN_HEIGHT_PX,
          maxHeight: 'none',
          overflow: 'visible',
        };
      }
      if (layoutTranscriptPosition === 'below') {
        const playerWidth = getPlayerWidth();
        const belowH = getEffectiveBelowTranscriptHeight();
        const belowMin = fullscreenLayoutZoomActive
          ? FULLSCREEN_ZOOM_BELOW_TRANSCRIPT_MIN_PX
          : TRANSCRIPT_MIN_HEIGHT_PX;
        // Event-modal Selected Media: fill the fit box. Matching only the (often
        // narrower, self-centered) video frame left a dead band on the right of the
        // bordered player when transcript was stacked below.
        return {
          width:
            isEventModal || isAudioFile || playerWidth <= 0 ? '100%' : `${playerWidth}px`,
          flex: '1 1 auto',
          minHeight: belowMin,
          maxHeight: `${belowH}px`,
          height: `${belowH}px`,
          overflow: 'auto',
        };
      }
      // Generic (non-presentation/event-modal) side layout — e.g. the SimplifiedSources
      // citation preview. Mirrors the presentation/fullscreen side style: share the row
      // with the player via flex-basis 0 instead of an unconstrained content width,
      // which let the video's CSS width collapse against the transcript (TR-1589 follow-up).
      // Same definite-pixel-height reasoning as the presentation branch above — this
      // context also has no definite ancestor height for align-self:'stretch' to
      // resolve against, so match the actual rendered player block height instead.
      const genericSideHeight = getPlayerHeight();
      const genericSideHeightPx = genericSideHeight > 0 ? genericSideHeight : TRANSCRIPT_MIN_HEIGHT_PX;
      const genericSideSplitStyle = sideSplitTranscriptStyle(genericSideHeightPx);
      if (genericSideSplitStyle) {
        return genericSideSplitStyle;
      }
      return {
        flex: '1 1 0',
        minWidth: 245,
        // See the isFullscreen/presentation side branches — same portrait-video cap
        // so the video reads as the dominant element here too (TR-1589 follow-up).
        maxWidth: '42%',
        height: `${genericSideHeightPx}px`,
        maxHeight: `${genericSideHeightPx}px`,
        overflow: 'hidden',
      };
    }

    if (isAudioFile) {
      if (layoutTranscriptPosition === 'below') {
        return {
          width: '100%',
          flex: 1,
          minHeight: TRANSCRIPT_MIN_HEIGHT_PX,
          height: 'auto',
        };
      }
      return {
        flex: 1,
        minWidth: 245,
        maxWidth: 500,
        height: '100%',
        alignSelf: 'stretch',
      };
    }

    if (layoutTranscriptPosition === 'side') {
      const splitStyle = sideSplitTranscriptStyle();
      if (splitStyle) {
        return splitStyle;
      }
      // Stretch to the MediaPlayer row (full MediaDisplay body). Use height:auto +
      // align-self:stretch — height:100% collapses if the row was content-sized.
      //
      // maxWidth cap: the video shell is w-max (content-sized to its own computed
      // pixel width, see playerShellClassName), and that computed width already
      // shrinks well below "row width − reserve" whenever handleLoadedMetadata's
      // height-cap kicks in (portrait/tall-aspect video squeezed into a shorter
      // fitHeight budget recomputes a much narrower finalW). This flex-1 transcript
      // then greedily claims *all* of whatever's left in the row — which, in true
      // fullscreen, transcript.tsx's expandSideWidth intentionally removes its own
      // 500px cap for, so it could balloon well past the video's width. Rob wants
      // the video to always read as the dominant element, so cap the transcript's
      // share here regardless of how narrow the video ends up (TR-1589 follow-up).
      // Editing (non-fullscreen) is untouched — Controls' toggle/below layout still
      // let the transcript take as much room as it needs there.
      return {
        flex: '1 1 0',
        minWidth: 245,
        maxWidth: '42%',
        minHeight: 0,
        height: '100%',
        maxHeight: 'none',
        alignSelf: 'stretch',
        overflow: 'hidden',
      };
    }

    return {
      width: `${getPlayerWidth()}px`,
      flexShrink: 0,
      flexGrow: 0,
      height: `${getEffectiveBelowTranscriptHeight()}px`,
      minHeight: TRANSCRIPT_MIN_HEIGHT_PX,
    };
  };

  if (!usableMediaSrc && !canRenderStillFrameThumbnail) return (
    <div className={`flex w-full items-center justify-center ${isCompactHorizontalAudio ? 'py-2' : 'h-full min-h-[120px]'}`}>
      <div className="flex flex-row items-center justify-center gap-2">
        <SpinnerGapIcon size={25} className='animate-spin-slow animate-pulse' />
        <span className='text-medium animate-pulse'>{t('media.loadingMedia')}</span>
      </div>
    </div>
  );


  const transcriptPanel = hasVisibleTranscript ? (
    <Transcript
      mediaItem={mediaItem}
      position={layoutTranscriptPosition}
      isPortrait={isPortrait}
      playerHeight={getPlayerHeight()}
      playerWidth={getPlayerWidth()}
      transcriptRef={transcriptRef}
      transcript={transcript}
      filteredTranscript={filteredTranscript}
      activeSegmentIndex={activeSegmentIndex}
      handleSegmentClick={handleSegmentClick}
      searchQuery={searchQuery}
      handleSearchChange={handleSearchChange}
      isSearchInputVisible={isSearchInputVisible}
      toggleSearchInputVisibility={toggleSearchInputVisibility}
      searchInputRef={searchInputRef}
      transcriptSrc={transcriptSrc}
      handleDownload={handleDownloadWithIntercomFlag}
      isFullscreen={isFullscreen}
      expandSideWidth={isFullscreen && !isAudioFile && layoutTranscriptPosition === 'side'}
      style={getTranscriptLayoutStyle()}
      autoScroll={autoScroll}
      onBelowTranscriptResizeStart={unlockBelowStackSplit}
      onBelowTranscriptResizeEnd={commitUserResizedBelowStackBudget}
    />
  ) : null;

  // items-stretch (not items-center): Track/Controls have no intrinsic width
  // (scrubber children are position:absolute), so centering collapses them to ~0.
  // Video stays centered via self-center on the video centerer.
  // Audio has no computed frame width. The side shell's `w-max` then collapses
  // to the scrubber's min-content, the bar measures under the minimal-controls
  // threshold, and volume / speed / settings unmount. Flex the column instead.
  const audioBesideTranscript =
    isAudioFile && hasVisibleTranscript && layoutTranscriptPosition === 'side';

  const playerShellClassName = `box-border relative flex flex-col items-stretch p-0 ${
    isCompactHorizontalAudio
      ? 'h-auto w-full'
      : isHorizontalTimeline
      ? `w-full rounded-sm ${MEDIA_PREVIEW_SURFACE_BG}`
      : isCompactThumbnail || isGoingToRenderSetStillFramePrompt || isFullscreen
      ? (isAudioFile && !isCompactThumbnail ? 'pt-[18px]' : 'pt-0')
      : isPresentation
        ? 'pt-0'
        : 'pt-4'
  }
    ${isTable || (hideMediaChrome && isUnifiedSources) ? '' : 'rounded-t-none rounded-b-lg'}
    ${isTable || isAccordion || isHorizontalTimeline || (hideMediaChrome && isUnifiedSources) ? '' : `${MEDIA_PREVIEW_SURFACE_BG} border-1 border-b-3 border-gray-700 border-solid`}
    ${isFullscreen
      ? // Fit within the fullscreen slot so track/controls stay on-screen at zoom 0.
        // Zoomed content may grow; the parent scrollport handles overflow.
        // Side transcript: self-start so the video column stays content-sized while
        // the transcript stretches to full MediaDisplay height.
        // 'below' stacks the row as a column, where align-self is horizontal: the
        // w-max shell needs self-center there or a portrait video hugs the left (TR-3394).
        `${useBelowTranscriptLayout ? 'shrink-0 ' : ''}${layoutTranscriptPosition === 'below' ? 'self-center ' : 'self-start '}max-h-full h-auto ${isAudioFile ? 'w-full' : 'w-max max-w-full'}`
      : hideMediaChrome && isUnifiedSources
        ? 'h-full w-full items-center justify-center'
      : isPresentation
        ? layoutTranscriptPosition === 'side' && hasVisibleTranscript
          // Side: content-sized (w-max), not flex-1. The video's actual pixel width
          // is computed in handleLoadedMetadata as (row width − transcript reserve),
          // i.e. sized to take up *most* of the row, not an even 50/50 split. Giving
          // this shell flex-1 forced it to ~half the row while its child (the video
          // centerer) still carried that wider explicit pixel width, so the video
          // overflowed its own shell and got clipped by the shell's overflow-hidden.
          // w-max (matches the working fullscreen-side pattern below) makes the
          // shell hug the video's real computed width instead, and the transcript's
          // own flex-1 then naturally claims whatever's left (Bugbot follow-up,
          // TR-1589).
          //
          // Gated on hasVisibleTranscript, not just layoutTranscriptPosition — side
          // is the default even when there is no transcript panel to actually render
          // (hidden/missing/still-loading). Without this gate, a plain video with no
          // transcript got a max-content shell with nothing beside it, and the video's
          // own `width: 100%` fit path resolved that percentage against this now-
          // indefinite (max-content) parent, collapsing/mis-sizing the player
          // (Bugbot follow-up, TR-1589).
          ? 'h-auto w-max max-w-full justify-start self-stretch'
          : 'h-auto w-full max-w-full min-w-0 shrink-0 justify-start self-stretch'
        : useEventModalSideLayout
          ? 'self-start shrink-0 h-auto justify-center max-w-full'
          : layoutTranscriptPosition === 'side' && hasVisibleTranscript
            // Generic side layout (e.g. SimplifiedSources preview): same w-max
            // reasoning (and hasVisibleTranscript gating) as the presentation side
            // branch above.
            ? 'h-auto w-max max-w-full justify-start self-stretch'
            : `${useBelowTranscriptLayout ? 'shrink-0 ' : ''}w-full h-auto justify-center`}
  `;

  const splitLayoutDuringDrag = belowStackSplitDragging
    ? belowStackSplitLayoutRef.current
    : sideStackSplitDragging
      ? sideStackSplitLayoutRef.current
      : null;
  const effectiveVideoCentererStyle = splitLayoutDuringDrag?.centerer ?? videoCentererStyle;
  const effectiveVideoStyle = splitLayoutDuringDrag?.video ?? videoStyle;

  const playerBlock = (
    <div
      ref={playerRef}
      className={playerShellClassName}
      style={
        useEventModalSideLayout
          ? {
              width: `${EVENT_MODAL_SIDE_PLAYER_WIDTH_PX}px`,
              maxWidth: '100%',
              minWidth: audioBesideTranscript ? AUDIO_SIDE_PLAYER_MIN_WIDTH_PX : 0,
              flexShrink: audioBesideTranscript ? 0 : 1,
            }
          : audioBesideTranscript
            ? {
                // Grow factors split the row by ratio (even until dragged); both
                // columns keep a minimum so a narrower row shrinks them together.
                flex: `${audioSideSplitRatio ?? 0.5} 1 0%`,
                width: 'auto',
                minWidth: SIDE_PLAYER_MIN_WIDTH_PX,
                maxWidth: 'none',
                ...(audioSideDragMinHeightPx != null
                  ? { minHeight: `${audioSideDragMinHeightPx}px` }
                  : pinnedPresentationPlayerOuterPx != null && pinnedPresentationPlayerOuterPx > 0
                    ? { minHeight: `${pinnedPresentationPlayerOuterPx}px` }
                    : {}),
              }
          : pinnedSidePlayerWidthPx != null && pinnedSidePlayerWidthPx > 0
            ? {
                width: `${pinnedSidePlayerWidthPx}px`,
                maxWidth: `${pinnedSidePlayerWidthPx}px`,
                flexShrink: 0,
              }
            : pinnedPresentationPlayerOuterPx != null && pinnedPresentationPlayerOuterPx > 0
              ? {
                  minHeight: `${pinnedPresentationPlayerOuterPx}px`,
                }
              : undefined
      }
    >
        {isAudioFile ? (
          <>
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen && !isHorizontalTimeline ? (
              <div className="flex flex-col items-center justify-center">
                <PlayIcon size={25} />
              </div>
            ) : (
              <audio
                ref={mediaRef as React.RefObject<HTMLAudioElement>}
                src={usableMediaSrc ?? undefined}
                className={isCompactHorizontalAudio ? 'sr-only' : 'rounded-md'}
                onLoadStart={handleLoadStart}
                onLoadedData={handleLoadedData}
                onLoadedMetadata={applyConfiguredStartTime}
                onError={handleMediaTagError}
                controls={false}
                style={isCompactHorizontalAudio ? undefined : { height: '100%', width: '100%' }}
              />
            )}
          </>
        ) : (
          <>
            {(isUnifiedSources || isAccordion || isTable) && !isHorizontalTimeline ? (
              <>
                {stillFrameImage ? (
                  <div
                    ref={stillFrameCentererRef}
                    className={`relative flex flex-row items-center justify-center overflow-hidden object-contain
                       ${isTable || isUnifiedSources ? 'h-full w-full' : ''}
                    `}
                  >
                    <img
                      ref={stillFrameRef}
                      src={stillFrameDisplaySrc}
                      onLoad={(e) => {
                        checkStillFrameSize();
                      }}
                      alt={t('media.stillFrame')}
                      className={`rounded-md object-contain ${isTable || isUnifiedSources ? 'h-full w-full' : 'max-h-full max-w-full'}`}
                      style={
                        stillFrameIsCaptureBake
                          ? undefined
                          : { transform: `rotate(${totalRotation}deg)` }
                      }
                    />
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-row items-center justify-center text-white">
                      <PlayIcon size={20} weight="fill"/>
                    </div>
                  </div>
                ) : (
                  <div className={`flex flex-col items-center justify-center${hideMediaChrome && isUnifiedSources ? ' size-full' : ''}`}>
                    <PlayIcon size={25} />
                    {!isAccordion && (
                      <span className="text-xs text-center whitespace-normal px-2">{t('media.addPreview')}</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {isLoading && (
                  <div
                    className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center"
                  >
                    <div className={`
                      flex flex-row items-center justify-center gap-2 text-sm animate-pulse text-center
                      ${isAccordion || isTable ? 'text-xs' : ''}
                    `}>
                      <SpinnerGapIcon size={20} className="animate-spin-slow" />
                      {t('media.loadingMediaEllipsis')}
                    </div>
                  </div>
                )}
                <div
                  ref={videoCentererRef}
                  className={`relative flex flex-row items-center justify-center ${
                    isHorizontalTimeline
                      ? 'h-auto w-full max-w-full min-w-0'
                      : isPresentation && !isFullscreen
                      ? 'h-auto w-full max-w-full min-w-0 shrink-0 self-stretch'
                      : 'self-center'
                  }`}
                  style={effectiveVideoCentererStyle}
                >
                  <video
                    className={`rounded-md ${MEDIA_PREVIEW_SURFACE_BG} ${isHorizontalTimeline ? 'max-h-[150px] w-full object-contain' : ''}`}
                    style={effectiveVideoStyle}
                    ref={mediaRef as React.RefObject<HTMLVideoElement>}
                    src={usableMediaSrc ?? undefined}
                    onLoadStart={handleLoadStart}
                    onLoadedData={handleLoadedData}
                    onLoadedMetadata={handleLoadedMetadata}
                    onError={handleMediaTagError}
                    controls={false}
                  >
                    <source src={usableMediaSrc ?? undefined} />
                  </video>
                </div>
              </>
            )}
          </>
        )}
        {!useStillFrame && !isUnifiedSources && !hideMediaChrome && !isCompactHorizontalAudio && (
          <div
            className={`${isPresentation && !isFullscreen ? 'w-full shrink-0' : ''} ${captureMode ? 'pointer-events-none' : ''}`}
            aria-disabled={captureMode || undefined}
          >
          <Track
            currentTime={currentTime}
            duration={duration}
            handleTimeChange={handleTimeChange}
            handleTimelineClick={handleTimelineClick}
            handleMouseMove={handleMouseMove}
            handleMouseLeave={handleMouseLeave}
            hoverTime={hoverTime}
            filteredTranscript={filteredTranscript}
            activeSegmentIndex={activeSegmentIndex}
            handleSegmentClick={handleSegmentClick}
            showTranscriptMarkers={showTranscriptMarkersState}
            startTime={mediaItem?.mediaContext?.startTime ? getSecondsFromTimeString(mediaItem.mediaContext.startTime) : undefined}
            endTime={mediaItem?.mediaContext?.endTime && mediaItem?.mediaContext?.endTime !== '0' ? getSecondsFromTimeString(mediaItem.mediaContext.endTime) : undefined}
            updateMediaContext={handleUpdateMediaContext}
            mediaItem={mediaItem}
            isPlaying={isPlaying}
            isEditingMediaContext={isEditingMediaContext}
            mediaRef={mediaRef}
            setCurrentTime={seekToTime}
            onTrimDragActiveChange={setTrimDragActive}
            disabled={captureMode}
          />
          </div>
        )}
        {!useStillFrame && !isUnifiedSources && !hideMediaChrome && (
          <div
            className={`${isPresentation && !isFullscreen ? 'w-full shrink-0' : ''} ${captureMode ? 'pointer-events-none' : ''}`}
            aria-disabled={captureMode || undefined}
          >
            <Controls
              isPlaying={isPlaying}
              togglePlay={togglePlay}
              isMuted={isMuted}
              toggleMute={toggleMute}
              volume={volume}
              handleVolumeChange={handleVolumeChange}
              currentTime={formatTimeIndex(currentTime.toString(), isEditingMediaContext ? 3 : 0) || '00:00'}
              duration={duration}
              isMenuVisible={isMenuVisible}
              toggleMenuVisibility={toggleMenuVisibility}
              playbackRate={playbackRate}
              handlePlaybackRateChange={handlePlaybackRateChange}
              showTranscript={showTranscriptState}
              toggleTranscriptVisibility={toggleTranscriptVisibility}
              showTranscriptMarkers={showTranscriptMarkersState}
              toggleTranscriptMarkersVisibility={toggleTranscriptMarkersVisibility}
              transcriptText={transcriptText}
              transcriptAvailable={transcript && transcript.length > 0}
              menuRef={menuRef}
              changeTranscriptPosition={changeTranscriptPosition}
              currentPosition={transcriptPosition}
              transcriptSrc={transcriptSrc}
              setStillFrame={setStillFrame}
              clearStillFrame={clearStillFrame}
              hasStillFrameImage={!!stillFrameImage}
              setStartTime={handleSetStartTime}
              setEndTime={handleSetEndTime}
              mediaItem={mediaItem}
              autoScroll={autoScroll}
              toggleAutoScroll={toggleAutoScroll}
              updateMediaContext={updateMediaContext || (() => {})}
              setIsEditingMediaContext={setIsEditingMediaContext}
              isEditingMediaContext={isEditingMediaContext}
              isEditing={isEditing}
              compact={isCompactHorizontalAudio}
              onRestoreTrimTimes={restoreTrimTimesPreview}
              disabled={captureMode}
            />
          </div>
        )}
    </div>
  );

  const handleBelowSplitPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      !useBelowTranscriptLayout
      || !containerRef.current
      || !transcriptRef.current
      || !playerRef.current
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    window.getSelection()?.removeAllRanges();

    const container = containerRef.current;
    const playerEl = playerRef.current;
    const transcriptEl = transcriptRef.current;
    const videoCenterer = videoCentererRef.current;
    const video = mediaRef.current as HTMLVideoElement | null;

    const playerBlockH = Math.round(playerEl.getBoundingClientRect().height);
    const startTranscript = Math.round(transcriptEl.getBoundingClientRect().height);
    const startVideoFrameH = videoCenterer
      ? Math.round(videoCenterer.getBoundingClientRect().height)
      : 0;
    // Always the live stack. The between-panel split only *redistributes* inside a
    // container it pins to this height, so widening it to a remembered budget would
    // snap a previously-larger stack back and lock empty space in below.
    // A grip expansion needs no help here — it already grew what we measure.
    const stackOuter = playerBlockH + BELOW_STACK_SPLITTER_TOTAL_PX + startTranscript;

    const chromePx = Math.max(
      PRESENTATION_PLAYER_CHROME_RESERVE_PX,
      playerBlockH - startVideoFrameH,
    );
    belowStackChromePxRef.current = chromePx;
    // Budget against the chrome actually inside `playerBlockH`, never the reserve
    // floor in `chromePx` — on a short stack that floor exceeds the real chrome and
    // drops the cap below the live transcript. Floored at `startTranscript` so
    // grabbing the split bar can never shrink the transcript on its own (the video
    // frame can measure 0 for audio, which underruns the cap by the video minimum).
    const playerChromeInStackPx = Math.max(0, playerBlockH - startVideoFrameH);
    belowStackMaxTranscriptPxRef.current = Math.max(
      SOFT_BELOW_TRANSCRIPT_MIN_PX,
      startTranscript,
      stackOuter - BELOW_STACK_SPLITTER_TOTAL_PX - playerChromeInStackPx - SOFT_BELOW_VIDEO_MIN_PX,
    );

    pinnedBelowStackOuterHeightRef.current = stackOuter;
    belowStackSplitLockedRef.current = true;
    belowStackDragTranscriptPxRef.current = startTranscript;

    flushSync(() => {
      setBelowStackPinnedOuterHeightPx(stackOuter);
      setBelowStackTranscriptPx(startTranscript);
      setBelowStackSplitDragging(true);
    });

    container.style.height = `${stackOuter}px`;
    container.style.maxHeight = `${stackOuter}px`;
    container.style.minHeight = `${stackOuter}px`;
    container.style.overflow = 'hidden';

    const startY = event.clientY;
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);

    const rotationDeg = Number(totalRotation) || 0;
    const swapWidthAndHeight =
      rotationDeg === 90 || rotationDeg === -90 || rotationDeg === 270;
    const videoRatio =
      video && video.videoWidth > 0 && video.videoHeight > 0
        ? swapWidthAndHeight
          ? video.videoHeight / video.videoWidth
          : video.videoWidth / video.videoHeight
        : 0;
    const columnWidth = Math.round(container.getBoundingClientRect().width);

    const applyBelowStackSplitDOM = (nextTranscript: number) => {
      const clamped = Math.max(
        SOFT_BELOW_TRANSCRIPT_MIN_PX,
        Math.min(belowStackMaxTranscriptPxRef.current, Math.round(nextTranscript)),
      );
      const deltaTranscript = clamped - startTranscript;
      const videoCap = startVideoFrameH - deltaTranscript;
      if (videoCap < SOFT_BELOW_VIDEO_MIN_PX) return;

      belowStackDragTranscriptPxRef.current = clamped;

      transcriptEl.style.height = `${clamped}px`;
      transcriptEl.style.minHeight = `${clamped}px`;
      transcriptEl.style.maxHeight = `${clamped}px`;

      let centererStyle: React.CSSProperties = videoCentererStyle;
      let nextVideoStyle: React.CSSProperties = videoStyle;

      if (videoCenterer && video && videoRatio > 0) {
        let finalH = videoCap;
        let finalW = Math.round(finalH * videoRatio);
        if (columnWidth > 0 && finalW > columnWidth) {
          finalW = columnWidth;
          finalH = Math.round(finalW / videoRatio);
        }
        const widthToUse = swapWidthAndHeight ? finalH : finalW;
        const heightToUse = swapWidthAndHeight ? finalW : finalH;
        centererStyle = {
          width: `${finalW}px`,
          height: `${finalH}px`,
          maxWidth: '100%',
          flexShrink: 0,
          overflow: 'hidden',
        };
        nextVideoStyle = {
          width: `${widthToUse}px`,
          height: `${heightToUse}px`,
          objectFit: 'contain',
          rotate: videoStyle.rotate,
        };
        videoCenterer.style.height = `${finalH}px`;
        videoCenterer.style.width = `${finalW}px`;
        videoCenterer.style.maxWidth = '100%';
        videoCenterer.style.flexShrink = '0';
        videoCenterer.style.overflow = 'hidden';
        video.style.width = `${widthToUse}px`;
        video.style.height = `${heightToUse}px`;
        video.style.objectFit = 'contain';
      }

      belowStackSplitLayoutRef.current = {
        transcriptPx: clamped,
        centerer: centererStyle,
        video: nextVideoStyle,
      };

      const stack = pinnedBelowStackOuterHeightRef.current ?? stackOuter;
      container.style.height = `${stack}px`;
      container.style.maxHeight = `${stack}px`;
      container.style.minHeight = `${stack}px`;
    };

    let splitDragRafId = 0;

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (splitDragRafId !== 0) return;
      splitDragRafId = window.requestAnimationFrame(() => {
        splitDragRafId = 0;
        const nextTranscript = startTranscript - (moveEvent.clientY - startY);
        applyBelowStackSplitDOM(nextTranscript);
        const clamped = belowStackDragTranscriptPxRef.current;
        if (clamped != null) {
          setBelowStackTranscriptPx(clamped);
        }
      });
    };

    const onPointerUp = (upEvent: PointerEvent) => {
      if (splitDragRafId !== 0) {
        window.cancelAnimationFrame(splitDragRafId);
        splitDragRafId = 0;
      }
      target.releasePointerCapture(upEvent.pointerId);
      applyBelowStackSplitDOM(startTranscript - (upEvent.clientY - startY));
      const finalTranscript =
        belowStackDragTranscriptPxRef.current ?? startTranscript;
      const committedLayout = belowStackSplitLayoutRef.current;
      flushSync(() => {
        setBelowStackTranscriptPx(finalTranscript);
        setBelowStackSplitDragging(false);
        if (committedLayout) {
          setVideoLayoutStyles(committedLayout.centerer, committedLayout.video);
        }
      });
      belowStackSplitLayoutRef.current = null;
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerup', onPointerUp);
      target.removeEventListener('pointercancel', onPointerUp);
    };

    target.addEventListener('pointermove', onPointerMove);
    target.addEventListener('pointerup', onPointerUp);
    target.addEventListener('pointercancel', onPointerUp);

    applyBelowStackSplitDOM(startTranscript);
  };

  const handleSideSplitPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      !useSideTranscriptLayout
      || !containerRef.current
      || !transcriptRef.current
      || !playerRef.current
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    window.getSelection()?.removeAllRanges();

    // Audio has no video frame. The shared pin measures the outer container,
    // locks its width, and clips overflow — pointerdown snaps the column
    // smaller and the divider then has no room to move. Resize the player
    // column directly and let the transcript take the rest of the row.
    if (isAudioFile) {
      const playerEl = playerRef.current;
      const transcriptEl = transcriptRef.current;
      const startPlayerW = Math.round(playerEl.getBoundingClientRect().width);
      const startTranscriptW = Math.round(transcriptEl.getBoundingClientRect().width);
      const totalW = startPlayerW + startTranscriptW;
      const startX = event.clientX;
      const maxPlayer = Math.max(
        SIDE_PLAYER_MIN_WIDTH_PX,
        totalW - SIDE_TRANSCRIPT_MIN_WIDTH_PX,
      );
      // Hold height only during the drag (min, not fixed) so controls that
      // stack at a narrower width can still grow the column.
      setAudioSideDragMinHeightPx(Math.round(playerEl.getBoundingClientRect().height));

      const applyAudioSplit = (clientX: number) => {
        if (totalW <= 0) return;
        const delta = clientX - startX;
        const nextPlayer = Math.min(
          maxPlayer,
          Math.max(SIDE_PLAYER_MIN_WIDTH_PX, Math.round(startPlayerW + delta)),
        );
        setAudioSideSplitRatio(nextPlayer / totalW);
      };

      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);

      const onPointerMove = (moveEvent: PointerEvent) => {
        applyAudioSplit(moveEvent.clientX);
      };
      const onPointerUp = (upEvent: PointerEvent) => {
        target.releasePointerCapture(upEvent.pointerId);
        applyAudioSplit(upEvent.clientX);
        setAudioSideDragMinHeightPx(null);
        target.removeEventListener('pointermove', onPointerMove);
        target.removeEventListener('pointerup', onPointerUp);
        target.removeEventListener('pointercancel', onPointerUp);
      };

      target.addEventListener('pointermove', onPointerMove);
      target.addEventListener('pointerup', onPointerUp);
      target.addEventListener('pointercancel', onPointerUp);
      return;
    }

    const container = containerRef.current;
    const playerEl = playerRef.current;
    const transcriptEl = transcriptRef.current;
    const videoCenterer = videoCentererRef.current;
    const video = mediaRef.current as HTMLVideoElement | null;

    const startPlayerW = Math.round(playerEl.getBoundingClientRect().width);
    const startTranscript = Math.round(transcriptEl.getBoundingClientRect().width);
    const startVideoFrameW = videoCenterer
      ? Math.round(videoCenterer.getBoundingClientRect().width)
      : startPlayerW;
    const startVideoFrameH = videoCenterer
      ? Math.round(videoCenterer.getBoundingClientRect().height)
      : 0;
    const startPlayerH = Math.round(playerEl.getBoundingClientRect().height);
    const stackOuter = Math.round(container.getBoundingClientRect().width);

    sideStackChromePxRef.current = Math.max(
      PRESENTATION_PLAYER_CHROME_RESERVE_PX,
      startPlayerH - startVideoFrameH,
    );
    // Preserve the player's current width. The row's real gap (splitter, flex
    // gap) often isn't SIDE_STACK_SPLITTER_TOTAL_PX, and forcing the audio
    // control-bar minimum here snapped the column to 340px and froze the drag.
    sideStackRowGapPxRef.current = stackOuter - startPlayerW - startTranscript;
    sideStackMaxTranscriptPxRef.current = Math.max(
      SIDE_TRANSCRIPT_MIN_WIDTH_PX,
      startTranscript + (startVideoFrameW - SIDE_PLAYER_MIN_WIDTH_PX),
    );

    pinnedSideStackOuterWidthRef.current = stackOuter;
    sideStackSplitLockedRef.current = true;
    sideStackDragTranscriptPxRef.current = startTranscript;

    flushSync(() => {
      setSideStackPinnedOuterWidthPx(stackOuter);
      setSideStackTranscriptPx(startTranscript);
      setSideStackSplitDragging(true);
    });

    container.style.width = `${stackOuter}px`;
    container.style.maxWidth = '100%';
    container.style.overflow = 'hidden';

    const startX = event.clientX;
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);

    const rotationDeg = Number(totalRotation) || 0;
    const swapWidthAndHeight =
      rotationDeg === 90 || rotationDeg === -90 || rotationDeg === 270;
    const videoRatio =
      video && video.videoWidth > 0 && video.videoHeight > 0
        ? swapWidthAndHeight
          ? video.videoHeight / video.videoWidth
          : video.videoWidth / video.videoHeight
        : 0;
    const rowHeight = Math.round(container.getBoundingClientRect().height);

    const applySideStackSplitDOM = (nextTranscript: number) => {
      const clamped = Math.max(
        SIDE_TRANSCRIPT_MIN_WIDTH_PX,
        Math.min(sideStackMaxTranscriptPxRef.current, Math.round(nextTranscript)),
      );
      const deltaTranscript = clamped - startTranscript;
      const videoCapW = startVideoFrameW - deltaTranscript;
      if (videoCapW < SIDE_PLAYER_MIN_WIDTH_PX) return;

      const nextPlayerW = stackOuter - sideStackRowGapPxRef.current - clamped;
      sideStackDragTranscriptPxRef.current = clamped;

      transcriptEl.style.width = `${clamped}px`;
      transcriptEl.style.minWidth = `${clamped}px`;
      transcriptEl.style.maxWidth = `${clamped}px`;
      transcriptEl.style.flex = '0 0 auto';

      playerEl.style.width = `${nextPlayerW}px`;
      playerEl.style.maxWidth = `${nextPlayerW}px`;
      playerEl.style.flexShrink = '0';

      let centererStyle: React.CSSProperties = videoCentererStyle;
      let nextVideoStyle: React.CSSProperties = videoStyle;

      if (videoCenterer && video && videoRatio > 0) {
        let finalW = videoCapW;
        let finalH = Math.round(finalW / videoRatio);
        if (rowHeight > 0) {
          const maxFrameH = Math.max(
            SOFT_BELOW_VIDEO_MIN_PX,
            rowHeight - sideStackChromePxRef.current,
          );
          if (finalH > maxFrameH) {
            finalH = maxFrameH;
            finalW = Math.round(finalH * videoRatio);
          }
        }
        const widthToUse = swapWidthAndHeight ? finalH : finalW;
        const heightToUse = swapWidthAndHeight ? finalW : finalH;
        centererStyle = {
          width: `${finalW}px`,
          height: `${finalH}px`,
          maxWidth: '100%',
          flexShrink: 0,
          overflow: 'hidden',
        };
        nextVideoStyle = {
          width: `${widthToUse}px`,
          height: `${heightToUse}px`,
          objectFit: 'contain',
          rotate: videoStyle.rotate,
        };
        videoCenterer.style.width = `${finalW}px`;
        videoCenterer.style.height = `${finalH}px`;
        videoCenterer.style.maxWidth = '100%';
        videoCenterer.style.flexShrink = '0';
        videoCenterer.style.overflow = 'hidden';
        video.style.width = `${widthToUse}px`;
        video.style.height = `${heightToUse}px`;
        video.style.objectFit = 'contain';
      }

      sideStackSplitLayoutRef.current = {
        transcriptPx: clamped,
        centerer: centererStyle,
        video: nextVideoStyle,
      };

      const stack = pinnedSideStackOuterWidthRef.current ?? stackOuter;
      container.style.width = `${stack}px`;
      container.style.maxWidth = '100%';
    };

    let splitDragRafId = 0;

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (splitDragRafId !== 0) return;
      splitDragRafId = window.requestAnimationFrame(() => {
        splitDragRafId = 0;
        const nextTranscript = startTranscript - (moveEvent.clientX - startX);
        applySideStackSplitDOM(nextTranscript);
        const clamped = sideStackDragTranscriptPxRef.current;
        if (clamped != null) {
          setSideStackTranscriptPx(clamped);
        }
      });
    };

    const onPointerUp = (upEvent: PointerEvent) => {
      if (splitDragRafId !== 0) {
        window.cancelAnimationFrame(splitDragRafId);
        splitDragRafId = 0;
      }
      target.releasePointerCapture(upEvent.pointerId);
      applySideStackSplitDOM(startTranscript - (upEvent.clientX - startX));
      const finalTranscript =
        sideStackDragTranscriptPxRef.current ?? startTranscript;
      const committedLayout = sideStackSplitLayoutRef.current;
      flushSync(() => {
        setSideStackTranscriptPx(finalTranscript);
        setSideStackSplitDragging(false);
        if (committedLayout) {
          setVideoLayoutStyles(committedLayout.centerer, committedLayout.video);
        }
      });
      sideStackSplitLayoutRef.current = null;
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerup', onPointerUp);
      target.removeEventListener('pointercancel', onPointerUp);
    };

    target.addEventListener('pointermove', onPointerMove);
    target.addEventListener('pointerup', onPointerUp);
    target.addEventListener('pointercancel', onPointerUp);

    applySideStackSplitDOM(startTranscript);
  };

  const belowStackSplitter = useBelowTranscriptLayout && hasVisibleTranscript ? (
    <div
      // `box-border` is load-bearing: `preflight` is off, so without it `py-0.5` sits
      // outside `h-7` and the bar renders 32px while every stack calculation reserves
      // BELOW_STACK_SPLITTER_TOTAL_PX (28) — 4px that has to come out of the video or
      // the transcript. The 18px caret still fits the 24px content box.
      className={`resizer-v group/resizer box-border flex flex-row items-center justify-center gap-1 w-full shrink-0 h-7 min-h-7 max-h-7 cursor-ns-resize m-0 px-0 py-0.5 rounded-full transition-colors duration-300 ${
        belowStackSplitDragging ? 'text-white resizing-active' : 'text-white/10 hover:text-white'
      }`}
      onPointerDown={handleBelowSplitPointerDown}
      style={{ touchAction: 'none' }}
      role="separator"
      aria-orientation="horizontal"
      aria-label={t('media.resizePanels')}
    >
      <button
        type="button"
        className={`flex h-1 w-10 shrink-0 rounded-full border-0 p-0 cursor-ns-resize transition-colors duration-300 ${
          belowStackSplitDragging ? 'bg-white' : 'bg-white/5 group-hover/resizer:bg-white'
        }`}
        aria-hidden
        tabIndex={-1}
      />
      <CaretCircleUpDownIcon size={18} weight="fill" className="shrink-0" />
      <button
        type="button"
        className={`flex h-1 w-10 shrink-0 rounded-full border-0 p-0 cursor-ns-resize transition-colors duration-300 ${
          belowStackSplitDragging ? 'bg-white' : 'bg-white/5 group-hover/resizer:bg-white'
        }`}
        aria-hidden
        tabIndex={-1}
      />
    </div>
  ) : null;

  const sideStackSplitter = useSideTranscriptLayout && hasVisibleTranscript ? (
    <div
      className={`resizer-h group/resizer flex flex-col items-center justify-start gap-1 self-stretch shrink-0 min-w-3 cursor-col-resize m-0 p-0 rounded-full transition-colors duration-300 ${
        sideStackSplitDragging ? 'text-white resizing-active' : 'text-white/10 hover:text-white'
      }`}
      onPointerDown={handleSideSplitPointerDown}
      style={{ touchAction: 'none' }}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('media.resizePanels')}
    >
      {/*
        Short fixed bar above the caret, then one flexible bar for the remaining
        height. `h-full` on both would resolve to the full stretched height each,
        pushing the caret out of the grip box entirely.
      */}
      <button
        type="button"
        className={`flex w-1 h-5 shrink-0 rounded-full border-0 p-0 cursor-col-resize transition-colors duration-300 ${
          sideStackSplitDragging ? 'bg-white' : 'bg-white/5 group-hover/resizer:bg-white'
        }`}
        aria-hidden
        tabIndex={-1}
      />
      <CaretCircleUpDownIcon size={18} weight="fill" className="rotate-90 shrink-0" />
      <button
        type="button"
        className={`flex w-1 flex-1 min-h-0 rounded-full border-0 p-0 cursor-col-resize transition-colors duration-300 ${
          sideStackSplitDragging ? 'bg-white' : 'bg-white/5 group-hover/resizer:bg-white'
        }`}
        aria-hidden
        tabIndex={-1}
      />
    </div>
  ) : null;

  return (
    <div
      ref={containerRef}
      data-testid="media-player-component"
      className={`flex box-border ${
        isCompactThumbnail
          ? 'h-full w-full items-center justify-center gap-0'
          : useFullscreenAudioLayout
            ? `h-full w-full flex-col${hasVisibleTranscript ? ' pb-[50px]' : ''}`
              : useFullscreenVideoTranscriptLayout
              ? // flex-1: fill MediaDisplay body under the toolbar. Without this the row
                // shrinks to the video frame and the side transcript cannot grow taller.
                // Below: overflow-y-auto so a reserve mismatch cannot permanently hide
                // the transcript drag handle (side stays clipped/stretch layout).
                // TR-3394: 'side' row uses justify-center (not justify-start) unless the
                // zoom scale can exceed 100% (fullscreenZoomMayOverflowRow — zoom IN
                // only, not zoom-out/resting). The transcript is capped at maxWidth 42%
                // (TR-1589) so a narrow portrait video's w-max shell + the capped
                // transcript often don't fill the row; justify-start dumped 100% of that
                // slack on the right, left-hugging the video. justify-center only
                // redistributes existing slack — it cannot widen an already-full (wide
                // landscape, or zoomed-in-and-overflowing) row, so it's safe whenever the
                // video cannot exceed its slot, including zoom-out (smaller than ever).
                // While zoomed IN past 100%, handleLoadedMetadata sets the video's
                // maxWidth to 'none' so it can outgrow this row, and this same row
                // switches to overflow-auto to scroll it. A centered flex item that
                // overflows its own container pushes half the overflow to the "start"
                // side, which scrollable flex containers cannot reach (no negative
                // scrollLeft) — only the "end"-side overflow is reachable. So keep
                // justify-start there, preserving the scrollport-reachable-from-origin
                // behavior for that specific zoom-in case only.
                `flex min-h-0 max-h-full w-full max-w-full flex-1 self-stretch pb-[50px] ${
                  layoutTranscriptPosition === 'below'
                    ? 'flex-col items-stretch justify-start gap-0 overflow-hidden'
                    : `h-full flex-row items-stretch ${fullscreenZoomMayOverflowRow ? 'justify-start' : 'justify-center'} ${sideLayoutRowGap} ${
                        fullscreenZoomMayOverflowRow
                          ? 'overflow-auto custom-scrollbar2'
                          : 'overflow-hidden'
                      }`
                }`
              // SourceCard before useBelowTranscriptLayout: content-sized column so a
              // transcript cannot flex-shrink the full-width video; SourceCard scrolls.
              // 'side': row instead, so an explicit user/mediaContext choice is honored
              // instead of always stacking (TR-1589 follow-up, GitLab-reported regression).
              : isPresentation && !isFullscreen
                ? `flex h-auto min-h-min min-w-0 w-full max-w-full shrink-0 items-stretch justify-start self-stretch ${
                    layoutTranscriptPosition === 'side' ? `flex-row ${sideLayoutRowGap}` : 'flex-col gap-0'
                  }`
              : useEventModalSideLayout
                ? 'flex h-auto w-full max-w-full min-h-0 flex-row items-stretch justify-start gap-4 overflow-hidden'
              : useBelowTranscriptLayout
                ? 'flex flex-col w-full max-h-full min-h-0 gap-0 items-stretch justify-start overflow-hidden'
                // 'side': items-stretch (not items-start) so the video shell's flex-1
                // can actually claim its share of row width instead of collapsing to
                // its unstretched content size (TR-1589 follow-up).
                // TR-3394: in fullscreen with no transcript, use justify-center so a
                // narrow vertical video is horizontally centered (not left-aligned).
                // justify-start is preserved for all non-fullscreen layouts and for
                // fullscreen-with-transcript (handled by useFullscreenVideoTranscriptLayout
                // above), where the video intentionally sits on the left.
                : `flex h-auto w-full items-stretch ${isFullscreen && !hasVisibleTranscript ? 'justify-center' : 'justify-start'} ${
                    layoutTranscriptPosition === 'below' ? 'flex-col gap-0' : `flex-row ${sideLayoutRowGap}`
                  }`
      }`}
      style={
        useFullscreenAudioLayout
          ? {
              boxSizing: 'border-box',
              height: '100%',
              paddingBottom: `${FULLSCREEN_MEDIA_VERTICAL_PADDING_RATIO * 100}%`,
              paddingTop: `${FULLSCREEN_MEDIA_VERTICAL_PADDING_RATIO * 100}%`,
              width: '100%',
            }
          : useSideTranscriptLayout && sideStackPinnedOuterWidthPx != null
            ? {
                width: `${sideStackPinnedOuterWidthPx}px`,
                maxWidth: '100%',
                overflow: 'hidden',
                ...(useFullscreenVideoTranscriptLayout
                  ? {
                      height: '100%',
                      minHeight: 0,
                      flex: '1 1 auto',
                      alignSelf: 'stretch',
                    }
                  : { flex: '0 0 auto' }),
              }
          : useBelowTranscriptLayout && belowStackPinnedOuterHeightPx != null
            ? {
                height: `${belowStackPinnedOuterHeightPx}px`,
                maxHeight: `${belowStackPinnedOuterHeightPx}px`,
                minHeight: `${belowStackPinnedOuterHeightPx}px`,
                overflow: 'hidden',
                flex: '0 0 auto',
              }
          : isPresentation && !isFullscreen
            ? {
                height: 'auto',
                flex: '0 0 auto',
                maxHeight: 'none',
                overflow: useBelowTranscriptLayout ? 'hidden' : undefined,
              }
          : useBelowTranscriptLayout && !isFullscreen
            ? {
                // Cap the whole player+transcript stack so a tall video cannot push
                // the transcript past the window bottom.
                maxHeight: 'calc(100vh - 120px)',
              }
            : undefined
      }
    >
      {useFullscreenAudioLayout ? (
        <div
          className={`min-h-0 flex-1 w-full ${
            layoutTranscriptPosition === 'below'
              ? 'flex flex-col gap-0'
              : 'flex flex-row items-stretch gap-4'
          }`}
        >
          {playerBlock}
          {belowStackSplitter}
          {sideStackSplitter}
          {transcriptPanel}
        </div>
      ) : (
        <>
          {playerBlock}
          {belowStackSplitter}
          {sideStackSplitter}
          {transcriptPanel}
        </>
      )}
    </div>
  );
};

MediaPlayer.displayName = 'MediaPlayer';

export default React.memo(MediaPlayer); 